// Samsung Tizen Cast-/OTT-Integration (TEP Web APIs).
//
// Deckt die im Samsung-Partner-Training ("EDO Google cast Training") gezeigten
// Bausteine ab:
//   - WAS Token + App-Installation + App-Launch (Netflix/YouTube/... via TEP)
//   - Google Cast (initialize / acceptTermsOfService / launchSettings / ...)
//   - SoftAP (SSID/Key setzen, TV-Name lesen, State-Listener) -> WLAN-QR
//   - Firmware Update (systemcontrol.updateFirmware)
//   - Settings Cloning (clone.restoreCloneData) inkl. App-Nachinstallation
//   - Clear Credentials (systemcontrol.deleteAppPrivateInfo)
//
// Alle Aufrufe sind defensiv gekapselt: fehlt eine API (aeltere Firmware /
// Browser-Test), wird geloggt und ein rejektierendes/leeres Ergebnis geliefert,
// statt die App zu werfen. Primaerziel laut Vorgabe ist Samsung.

import { rankAppMatches } from './appMatch';

const LIB = 'webapis'; // Samsung TEP APIs haengen unter window.webapis

function api() {
  return typeof window !== 'undefined' ? window[LIB] : undefined;
}

function was() {
  const w = api();
  return w && w.was ? w.was : undefined;
}

// Wie lange gcWhenReady auf das Ende von INIT wartet. Nach dem Hochlaufen
// braucht der Dienst einige Sekunden; danach wird es trotzdem versucht.
const GC_READY_TIMEOUT_MS = 20000;

// Unterhalb dieser Tizen-Version schliesst jeder Google-Cast-Aufruf mit
// finalize() ab - wie bis 6dce574, und so lief Cast am BU800 (Tizen 6.5).
// Seitdem blieb initialize() dort offen (Status bei jedem get_info,
// Usage-Bit, Warten auf READY, launchSettings), und der Dienst hing
// dauerhaft auf INIT: das Handy fand den TV nicht. Ab Tizen 7 bleibt es bei
// Samsungs Vorlage (kein finalize ausser bei ToS und Checkout), am U800F
// (Tizen 9) steht der Dienst so auf READY.
const GC_FINALIZE_BELOW_TIZEN = 7;

// Ausweichweg tizen.application.launch, wenn WAS den Start verweigert
// (eingefuehrt 23.09.2026, siehe launchApp).
//
// 05.10.2026 abgeschaltet: am BU800 startete YouTube so trotz
// ERROR_NOT_REGISTERED - am Token vorbei -, und die Anmeldung des Gastes blieb
// nach Roter Taste und Standby-Neustart bestehen.
// 07.10.2026 WIEDER EINGESCHALTET (Nutzer-Entscheidung): jede Cast-Kachel soll
// ihre App oeffnen, wenn sie auf dem TV vorhanden ist. Am U800F (Tizen 9)
// startet YouTube nur auf diesem Weg (WAS: ERROR_NOT_REGISTERED), und dort
// war die Anmeldung nach dem Standby-Neustart weg. Offener Vorbehalt: am
// BU800 (Tizen 6.5) ist das Loeschen solcher Apps nicht belegt.
const TIZEN_LAUNCH_FALLBACK = true;

// So lange darf der SoftAP nach einem fehlerfreien enableSoftAP brauchen, bis
// er als nicht einschaltbar gilt (U800F: deutlich ueber 1 s, unter 15 s).
const SOFTAP_UP_WAIT_MS = 12000;

function googlecast() {
  const w = api();
  return w && w.googlecast ? w.googlecast : undefined;
}

function systemcontrol() {
  const w = api();
  // b2bapis.b2bcontrol ist die aeltere Variante, systemcontrol die aktuelle.
  if (w && w.systemcontrol) return w.systemcontrol;
  if (typeof window !== 'undefined' && window.b2bapis && window.b2bapis.b2bcontrol)
    return window.b2bapis.b2bcontrol;
  return undefined;
}

function network() {
  const w = api();
  return w && w.network ? w.network : undefined;
}

function clone() {
  const w = api();
  return w && w.clone ? w.clone : undefined;
}

// Der Tizen-Application-Manager (tizen.application). Samsung-Support 09/2026
// nennt tizen.application.launch(appId) als den Weg, eine per Clone verteilte
// App zu starten - die Id ist die widget id aus der clone.xml. Privileg
// http://tizen.org/privilege/application.launch (config.tizen65.xml).
function tizenApp() {
  if (typeof window === 'undefined' || !window.tizen) return undefined;
  return window.tizen.application || undefined;
}

// Fehlercodes der WAS-API. Samsung liefert sie als message-String, nicht als
// code - ohne Uebersetzung steht auf der Install-Seite nur der rohe Bezeichner.
// Liste aus der Samsung-Referenz-App APPNetGC (Google-Cast/Netflix-Training).
// Namen, die bei webapis.clone in Frage kommen. Nur der Rueckfall, falls sich
// das API-Objekt nicht aufzaehlen laesst - die Aufzaehlung bleibt der
// eigentliche Weg, weil sie auch Namen findet, die hier niemand erraten haette.
// Die Schreibrichtung ist absichtlich breit geraten: genau darum geht es.
const CLONE_API_GUESSES = [
  'restoreCloneData',
  'storeCloneData',
  'saveCloneData',
  'getCloneData',
  'exportCloneData',
  'createCloneData',
  'cloneData',
  'setCloneUpdateStatusListener',
  'unSetCloneUpdateStatusListener',
];

const WAS_ERROR = {
  ERROR_TOKEN_ALREADY_INITIALIZED: 'Token bereits initialisiert (unkritisch)',
  ERROR_TOKEN_NOT_INITIALIZED: 'Token nicht initialisiert - initializeSIToken lief nicht oder schlug fehl',
  ERROR_TOKEN_INVALID: 'Token ungueltig - gehoert zu einem anderen Vertrag/Geraet',
  ERROR_TOKEN_NOT_UPDATED: 'Token nicht aktualisiert - der TV konnte ihn nicht uebernehmen',
  ERROR_NO_TOKEN_APP_DATA:
    'kein App-Datensatz zum Token - dieser Token schaltet diese App nicht frei',
  ERROR_APP_PERMISSION_DISABLE: 'App per Berechtigung gesperrt',
  ERROR_APP_PERMISSION_UNINSTALL: 'App per Berechtigung nur deinstallierbar',
  ERROR_NOT_REGISTERED: 'App am Geraet nicht registriert',
  ERROR_NOT_ALLOWED: 'Vorgang fuer diese App nicht erlaubt',
  FAIL: 'Aufruf fehlgeschlagen',
};

// Lesbarer Text aus einem Fehler bzw. einem Tizen-Fehlerobjekt. Ohne das steht
// in der Diagnose nur "[object Object]".
function errText(e) {
  if (!e) return 'unbekannter Fehler';
  const raw = typeof e === 'string' ? e : e.message || e.errorMessage || e.name || '';
  const known = WAS_ERROR[raw];
  if (known) return raw + ' (' + known + ')';
  if (typeof e === 'string') return e;
  const name = e.name || '';
  if (name && raw && name !== raw) return name + ': ' + raw;
  return String(name || raw || JSON.stringify(e));
}

function logErr(scope, e) {
  const code = (e && (e.code !== undefined ? e.code : '')) || '';
  const name = (e && e.name) || '';
  const message = (e && e.message) || String(e);
  console.log('[Cast/Tizen] ' + scope + ' error [' + code + '] ' + name + ': ' + message);
}

// SecurityError (Code 18): der installierten WGT fehlt das Privileg - am
// BU800 "TestHeikoDavid0705" (05.10.2026) antworteten ALLE Partner-APIs so
// (systemcontrol, network.softAP, timer, remotepower, WAS-Token). Das ist kein
// defekter Schalter, sondern ein Installations-/Zertifikatsproblem, und darf
// deshalb nie den dauerhaften Merker "Hotspot nicht neustartbar" setzen - der
// bliebe sonst auch nach einer korrekten Neuinstallation stehen.
function isSecurityError(e) {
  return !!e && (e.code === 18 || e.name === 'SecurityError');
}

export default class CastTizen {
  // Plattformversion des TV ("6.5", "7.0", "9.0", ...) bzw. '' wenn nicht lesbar.
  static getPlatformVersion() {
    try {
      if (typeof window !== 'undefined' && window.tizen && window.tizen.systeminfo) {
        return window.tizen.systeminfo.getCapabilities().platformVersion || '';
      }
    } catch (e) {
      logErr('getPlatformVersion', e);
    }
    return '';
  }

  // -------------------------------------------------------------------------
  // Support-Gate: Cast/OTT nur ab Tizen 6.5 (Vorgabe). Aeltere Orsay/6.0-Geraete
  // liefern keine googlecast/was-APIs.
  // -------------------------------------------------------------------------
  static isSupported() {
    try {
      var v = CastTizen.getPlatformVersion();
      // Numerischer Vergleich (6.5 -> 6.5), Fallback auf String-Vergleich.
      var num = parseFloat(v);
      if (!isNaN(num)) return num >= 6.5;
      return String(v) >= '6.5';
    } catch (e) {
      logErr('isSupported', e);
      // Wenn die googlecast-API existiert, ist das ein starkes Signal fuer Support.
      return !!googlecast();
    }
  }

  // =========================================================================
  // WAS: Token / Capability / Install / Launch
  // =========================================================================

  // Initialisiert den SI-Token. Muss beim ersten Start bzw. bei jedem neuen
  // Gast (Check-in) aufgerufen werden, bevor installApp/launchApp funktionieren.
  static initToken(token) {
    const w = was();
    if (!w) {
      logErr('initToken', 'webapis.was missing');
      CastTizen._tokenInit = false;
      CastTizen._tokenDetail = 'webapis.was nicht vorhanden';
      return false;
    }
    try {
      // Die API heisst je nach Firmware initializeSIToken bzw. SITokenInitialize.
      if (typeof w.initializeSIToken === 'function') {
        w.initializeSIToken(token);
      } else if (typeof w.SITokenInitialize === 'function') {
        w.SITokenInitialize(token);
      } else {
        logErr('initToken', 'no token init function');
        CastTizen._tokenInit = false;
        CastTizen._tokenDetail = 'weder initializeSIToken noch SITokenInitialize vorhanden';
        return false;
      }
      console.log('[Cast/Tizen] SI-Token initialisiert');
      CastTizen._tokenInit = true;
      CastTizen._tokenDetail = 'initialisiert';
      return true;
    } catch (e) {
      // ERROR_TOKEN_ALREADY_INITIALIZED ist unkritisch.
      if (e && (e.message === 'ERROR_TOKEN_ALREADY_INITIALIZED' || e.name === 'ERROR_TOKEN_ALREADY_INITIALIZED')) {
        console.log('[Cast/Tizen] SI-Token bereits initialisiert');
        CastTizen._tokenInit = true;
        CastTizen._tokenDetail = 'bereits initialisiert';
        return true;
      }
      logErr('initToken', e);
      CastTizen._tokenInit = false;
      CastTizen._tokenDetail = errText(e);
      return false;
    }
  }

  // Liefert die Capability eines App-Ids ("CAPABILITY_ENABLE" / "_DISABLE" /
  // "_UNINSTALL") oder null. Damit prueft man, ob z.B. Netflix erlaubt ist.
  //
  // Der Grund fuer ein null wird in _lastCapError festgehalten: ohne ihn meldet
  // die Install-Seite nur "keine Auskunft" und man sieht nicht, ob die API
  // fehlt, der Token nicht initialisiert ist oder der Aufruf geworfen hat.
  static getCapability(appId) {
    const w = was();
    CastTizen._lastCapError = '';
    if (!w) {
      CastTizen._lastCapError = 'webapis.was nicht vorhanden';
      return null;
    }
    if (typeof w.getCapability !== 'function') {
      CastTizen._lastCapError = 'webapis.was.getCapability nicht vorhanden';
      return null;
    }
    try {
      const cap = w.getCapability(appId);
      if (cap === undefined || cap === null || cap === '') {
        CastTizen._lastCapError = 'getCapability lieferte ' + String(cap);
        return null;
      }
      return cap;
    } catch (e) {
      logErr('getCapability(' + appId + ')', e);
      CastTizen._lastCapError = errText(e);
      return null;
    }
  }

  // Alle auf dem TV installierten Apps ueber die Tizen-Standard-API.
  //
  // Zweite, von WAS unabhaengige Quelle: liefert getCapability nichts (kein
  // Token, alte/abweichende Firmware), ist hier trotzdem zu sehen, ob die App
  // auf dem Geraet vorhanden ist. Braucht das Privileg
  // http://tizen.org/privilege/application.info in der config.xml - fehlt es,
  // kommt ein SecurityError, der genau so gemeldet wird.
  static getInstalledApps() {
    return new Promise((resolve, reject) => {
      const t = typeof window !== 'undefined' ? window.tizen : undefined;
      if (!t || !t.application || typeof t.application.getAppsInfo !== 'function') {
        reject(new Error('tizen.application.getAppsInfo nicht vorhanden'));
        return;
      }
      try {
        t.application.getAppsInfo(
          function(list) {
            resolve(
              (list || []).map(function(a) {
                return { id: a.id, name: a.name, packageId: a.packageId };
              })
            );
          },
          function(err) {
            reject(err || new Error('getAppsInfo error'));
          }
        );
      } catch (e) {
        reject(e);
      }
    });
  }

  // Ermittelt einmalig (bzw. bei force erneut), was die Geraete-APIs hergeben.
  // Ergebnis landet als Diagnose auf der Info- und der App-Install-Seite.
  static detect(force) {
    if (CastTizen._caps && !force) return Promise.resolve(CastTizen._caps);
    if (CastTizen._detecting && !force) return CastTizen._detecting;

    const w = api();
    const ws = was();
    const caps = {
      detected: true,
      platformVersion: CastTizen.getPlatformVersion(),
      supported: CastTizen.isSupported(),
      hasWebapis: !!w,
      hasWas: !!ws,
      hasGetCapability: !!(ws && typeof ws.getCapability === 'function'),
      hasInstallApp: !!(ws && typeof ws.installApp === 'function'),
      hasLaunchApp: !!(ws && typeof ws.launchApp === 'function'),
      hasGoogleCast: !!googlecast(),
      // Status des Dienstes und das Usage-Bit (opt_in.stats) - beide prueft
      // Samsungs Hotel-SI-Checkliste; so stehen sie ohne curl auf Port 7443
      // in der Diagnose.
      gcStatus: CastTizen.gcGetStatus(),
      gcUsageReporting: CastTizen.gcUsageReportingStatus(),
      hasSystemControl: !!systemcontrol(),
      hasNetwork: !!network(),
      // Welche SoftAP-Funktionen die Firmware wirklich mitbringt. Ohne das
      // laesst sich am Geraet nicht unterscheiden, ob ein Schreibzugriff
      // fehlschlug oder die Funktion gar nicht existiert.
      softApApi: CastTizen.softApApiNames(),
      // Vollstaendig, nicht nur die SoftAP-Namen: die Suche nach einer Auskunft
      // ueber verbundene Geraete ist auf den SoftAP-Namen ergebnislos geblieben.
      networkApi: CastTizen.networkApiNames(),
      // Was googlecast und WAS auf diesem Geraet anbieten. Gesucht (BU800,
      // 07.10.2026): (1) ein Setter fuer den Cast-Geraetenamen - das Handy
      // zeigte "HBU80009775" statt des Raumnamens; (2) ein Weg, einen
      // laufenden Cast bzw. die gecastete App zu beenden - nach HOME lief der
      // Ton von YouTube im Hintergrund weiter.
      gcApi: CastTizen.apiNames(googlecast()),
      wasApi: CastTizen.apiNames(was()),
      // Gibt es eine Schaltfunktion (aus/ein) und eine Client-Abfrage? Ohne die
      // erste wird nichts geschaltet, ohne die zweite haengt die
      // Verbindungsanzeige an den Ereignissen des TV.
      softApSwitch: (CastTizen._softApSwitch() || {}).name || null,
      // false heisst: enableSoftAP hat auf diesem Geraet schon versagt, es wird
      // nicht mehr geschaltet - und ein Passwortwechsel braucht einen Neustart.
      softApRestartable: !CastTizen.isSoftApSwitchBroken(),
      // Der Listener, der VERBUNDENE GERAETE meldet. Ohne ihn kann die
      // Cast-Seite nicht anzeigen, dass das Handy angekommen ist - und ohne
      // diese Zeile ist am Geraet nicht zu sehen, ob er fehlt oder nur nicht
      // feuert.
      softApClientInfoListener: CastTizen.hasSoftAPClientInfoListener(),
      // Ohne das gibt es nur das Vormerken fuer den naechsten Neustart.
      clearCredentialsNow: CastTizen.hasClearCredentialsNow(),
      softApClientQuery: CastTizen.softApClientFnNames(),
      // Was webapis.clone auf DIESEM Geraet kann.
      //
      // Die App nutzt bisher nur restoreCloneData (anwenden). Ob die Firmware
      // auch eine Gegenrichtung hat - ein Clonefile schreiben/auslesen, in
      // gesicherter oder ungesicherter Form -, laesst sich nicht aus dem
      // Quellcode beantworten: anders als hcap.js kommt diese API vom Geraet.
      // Deshalb dieselbe Antwort wie bei SoftAP und Netzwerk: nicht raten,
      // sondern die vollstaendige Liste auf /info zeigen.
      cloneApi: CastTizen.cloneApiNames(),
      // Gibt es webapis.clone ueberhaupt? Trennt "API fehlt" von "API da, aber
      // keine Funktion gefunden" - siehe cloneApiNames.
      hasClone: !!clone(),
      tokenInitialized: !!CastTizen._tokenInit,
      tokenDetail: CastTizen._tokenDetail || 'noch nicht aufgerufen',
      appList: null,
      appListError: '',
    };

    CastTizen._detecting = CastTizen.getInstalledApps()
      .then((list) => {
        caps.appList = list;
        return caps;
      })
      .catch((e) => {
        caps.appListError = errText(e);
        return caps;
      })
      .then((result) => {
        CastTizen._caps = result;
        CastTizen._detecting = null;
        console.log('[Cast/Tizen] detect:', result);
        return result;
      });
    return CastTizen._detecting;
  }

  static capabilities() {
    return CastTizen._caps || null;
  }

  // Sucht eine App im Verzeichnis des TV - erst ueber die Id, dann ueber Name
  // bzw. Aliasse (Ids weichen je Firmware/Region ab). Nebenvarianten
  // (YoutubeKids, Google Cast Settings, ...) gelten nicht als die App.
  static _findApp(list, appId, options) {
    const hit = CastTizen._findAllApps(list, appId, options).filter((h) => !h.variant)[0];
    return hit ? hit.entry : null;
  }

  // ALLE Eintraege am Geraet, die zu dieser App passen - nicht nur der erste.
  // Liefert { entry, variant }.
  //
  // Wichtig fuer die Frage, ob es sprachspezifische App-Ids ueberhaupt gibt:
  // haette ein TV z.B. eine deutsche und eine englische Netflix-Variante, waere
  // das nur hier zu sehen. Die konfigurierte Id kommt zuerst, danach die
  // Namens-/Alias-Treffer nach Rang (siehe appMatch.js).
  static _findAllApps(list, appId, options) {
    if (!list || !list.length) return [];
    const byId = appId ? list.filter((a) => a.id === appId || a.packageId === appId) : [];
    const hits = byId.map((a) => ({ entry: a, variant: false }));
    rankAppMatches(list, options).forEach((h) => {
      if (byId.indexOf(h.entry) === -1) hits.push(h);
    });
    return hits;
  }

  // App-Id einer installierten App ueber Name/aliases (siehe Cast.findInstalledAppId).
  static findInstalledAppId(options) {
    return CastTizen.detect().then((caps) => {
      const hit = CastTizen._findApp(caps && caps.appList, null, options);
      if (hit) console.log('[Cast/Tizen] App ueber den Namen gefunden: ' + (hit.name || '') + ' -> ' + hit.id);
      return hit ? hit.id : '';
    });
  }

  // Warum liefert WAS keine Auskunft? Liefert den konkretesten bekannten Grund.
  static _capReason(caps) {
    if (!caps.hasWebapis) return 'webapis fehlt (keine Samsung-B2B-API auf diesem Geraet)';
    if (!caps.hasWas) return 'webapis.was fehlt (Firmware ohne TEP/WAS)';
    if (!caps.hasGetCapability) return 'webapis.was.getCapability fehlt';
    if (!caps.tokenInitialized) return 'SI-Token nicht initialisiert (' + caps.tokenDetail + ')';
    return CastTizen._lastCapError || 'getCapability ohne Ergebnis';
  }

  // Zustand einer App fuer die Install-Seite. Primaerquelle ist die
  // WAS-Capability:
  //   CAPABILITY_ENABLE    -> installiert und erlaubt
  //   CAPABILITY_UNINSTALL -> nicht installiert, Installation moeglich
  //   CAPABILITY_DISABLE   -> vom Geraet gesperrt
  //
  // Schweigt WAS (alte Firmware, fehlender Token, fehlendes Privileg), wird auf
  // das App-Verzeichnis des TV zurueckgefallen, statt pauschal "keine Auskunft"
  // zu melden - dort ist wenigstens zu sehen, ob die App vorhanden ist. In
  // jedem Fall steht im Detail, WARUM die Auskunft so ausfaellt.
  static getInstallState(appId, options) {
    return CastTizen.detect().then((caps) => {
      // Ohne Id (App nur ueber den Namen bekannt) gibt es nichts zu fragen.
      const cap = appId ? CastTizen.getCapability(appId) : null;
      const allOnDevice = CastTizen._findAllApps(caps.appList, appId, options);
      const main = allOnDevice.filter((h) => !h.variant)[0];
      const onDevice = main ? main.entry : null;
      const resolved = onDevice && onDevice.id !== appId ? { resolvedAppId: onDevice.id } : {};
      // Alle Treffer mitgeben, nicht nur den benutzten - so wird auf /info-apps
      // sichtbar, ob das Geraet mehrere Varianten derselben App fuehrt.
      if (allOnDevice.length) {
        resolved.deviceMatches = allOnDevice.map((h) => ({
          id: h.entry.id,
          name: h.entry.name || '',
          variant: h.variant,
        }));
      }
      const variantsOnly = allOnDevice
        .filter((h) => h.variant)
        .map((h) => '"' + (h.entry.name || h.entry.id) + '"');

      if (cap === 'CAPABILITY_ENABLE') {
        return Object.assign({ state: 'installed', detail: 'WAS: ' + cap }, resolved);
      }
      if (cap === 'CAPABILITY_UNINSTALL') {
        return Object.assign({ state: 'installable', detail: 'WAS: ' + cap }, resolved);
      }
      if (cap === 'CAPABILITY_DISABLE') {
        return Object.assign(
          { state: 'blocked', detail: 'WAS: ' + cap + ' - im Hotelmenue/Clone-Profil gesperrt' },
          resolved
        );
      }

      const reason = CastTizen._capReason(caps);
      if (caps.appList) {
        if (onDevice) {
          return Object.assign(
            {
              state: 'installed',
              detail:
                'auf dem TV vorhanden als "' + (onDevice.name || onDevice.id) + '" (' + onDevice.id + ')' +
                ' - WAS ohne Auskunft: ' + reason,
            },
            resolved
          );
        }
        return Object.assign(
          {
            state: 'missing',
            detail:
              'nicht im App-Verzeichnis des TV (' + caps.appList.length + ' Apps gelesen)' +
              (variantsOnly.length ? ', nur Nebenvarianten: ' + variantsOnly.join(', ') : '') +
              ' - WAS ohne Auskunft: ' + reason,
          },
          resolved
        );
      }

      return {
        state: 'unknown',
        detail: reason + '; App-Verzeichnis nicht lesbar: ' + (caps.appListError || 'unbekannt'),
      };
    });
  }

  // Installiert eine App per WAS. Gibt ein Promise zurueck (resolve bei
  // oninstallsuccess, reject bei oninstallerror).
  static installApp(appId, onProgress) {
    return new Promise((resolve, reject) => {
      const w = was();
      if (!w || typeof w.installApp !== 'function') {
        reject(new Error('webapis.was.installApp missing'));
        return;
      }
      const listener = {
        oninstallstart: function(id) {
          console.log('[Cast/Tizen] install start ' + id);
          if (onProgress) onProgress({ phase: 'start', id: id });
        },
        ondownload: function(id, progress) {
          if (onProgress) onProgress({ phase: 'download', id: id, progress: progress });
        },
        oninstallation: function(id, progress) {
          if (onProgress) onProgress({ phase: 'install', id: id, progress: progress });
        },
        oninstallsuccess: function(id) {
          console.log('[Cast/Tizen] install success ' + id);
          resolve(id);
        },
        oninstallerror: function(id, error) {
          logErr('installApp(' + id + ')', error);
          reject(error || new Error('install error'));
        },
      };
      try {
        w.installApp(appId, listener);
      } catch (e) {
        logErr('installApp(' + appId + ')', e);
        reject(e);
      }
    });
  }

  // Startet eine App ueber den Tizen-Application-Manager statt ueber WAS.
  //
  // Der Ausweichweg fuer launchApp: fehlt webapis.was (kein was.partner-
  // Privileg, aeltere Firmware) oder verweigert WAS den Start, kann eine per
  // Clone installierte App so trotzdem starten. Ein Metadatum laesst sich hier
  // nicht mitgeben - fuer den Geraete-Browser taugt der Weg deshalb nicht.
  static canLaunchViaTizen() {
    const a = tizenApp();
    return !!(a && typeof a.launch === 'function');
  }

  // Ids fuer den Ausweichweg: die konfigurierte, danach die Id, unter der die
  // App im App-Verzeichnis des TV steht. WAS kennt die Store-Id
  // (Netflix 3201907018807), der Application-Manager dagegen nur die Id am
  // Geraet - auf einem BU800 ist Netflix eine Firmware-App namens
  // org.tizen.netflix-app. Mit der Store-Id allein endete der Ausweichweg dort
  // immer in "NotFoundError: No matched application found".
  static tizenLaunchIds(appId, options) {
    const opts = options || {};
    return CastTizen.detect()
      .catch(() => null)
      .then((caps) => {
        const ids = [appId];
        const hit =
          caps && (opts.name || opts.aliases)
            ? CastTizen._findApp(caps.appList, appId, { name: opts.name, aliases: opts.aliases })
            : null;
        if (hit && ids.indexOf(hit.id) === -1) {
          console.log('[Cast/Tizen] Ausweichweg auch ueber die Id am Geraet: ' + hit.id + ' (' + (hit.name || '') + ')');
          ids.push(hit.id);
        }
        return ids;
      });
  }

  static launchViaTizen(appId) {
    return new Promise((resolve, reject) => {
      const a = tizenApp();
      if (!a || typeof a.launch !== 'function') {
        reject(new Error('tizen.application.launch missing'));
        return;
      }
      try {
        a.launch(
          appId,
          function() {
            console.log('[Cast/Tizen] tizen.application.launch success ' + appId);
            resolve(appId);
          },
          function(error) {
            logErr('tizen.application.launch(' + appId + ')', error);
            reject(error || new Error('launch error'));
          }
        );
      } catch (e) {
        logErr('tizen.application.launch(' + appId + ')', e);
        reject(e);
      }
    });
  }

  // Startet eine App per WAS. Optional wird davor Capability geprueft und bei
  // fehlender Installation nachinstalliert. Scheitert WAS endgueltig (oder
  // fehlt es), folgt ein Versuch ueber tizen.application.launch - abgelehnt
  // wird dann mit dem URSPRUENGLICHEN WAS-Fehler, der ist aussagekraeftiger.
  static launchApp(appId, options) {
    const opts = options || {};
    const fallbackOk = TIZEN_LAUNCH_FALLBACK && !opts.metaData && CastTizen.canLaunchViaTizen();
    const viaTizen = (cause) => {
      if (!fallbackOk) {
        if (!TIZEN_LAUNCH_FALLBACK) {
          console.log(
            '[Cast/Tizen] WAS-Start von ' + appId + ' gescheitert (' + errText(cause) + ') - ' +
              'Ausweichweg tizen.application.launch ist abgeschaltet (TIZEN_LAUNCH_FALLBACK)'
          );
        }
        return Promise.reject(cause);
      }
      console.log('[Cast/Tizen] WAS-Start von ' + appId + ' gescheitert (' + errText(cause) + ') - versuche tizen.application.launch');
      return CastTizen.tizenLaunchIds(appId, opts)
        .then((ids) =>
          ids.reduce(
            (p, id) => p.catch(() => CastTizen.launchViaTizen(id)),
            Promise.reject(new Error('kein Kandidat'))
          )
        )
        .catch(() => Promise.reject(cause));
    };
    return new Promise((resolve, reject) => {
      const w = was();
      if (!w || typeof w.launchApp !== 'function') {
        viaTizen(new Error('webapis.was.launchApp missing')).then(resolve, reject);
        return;
      }
      const failed = (error) => viaTizen(error).then(resolve, reject);
      // Nachinstallieren, wenn der TV die App beim Start nicht findet - aber nur
      // einmal, sonst kreist es.
      let retried = false;
      const listener = {
        onlaunchstarted: function(id) {
          console.log('[Cast/Tizen] launch start ' + id);
        },
        onlaunchsuccess: function(id) {
          console.log('[Cast/Tizen] launch success ' + id);
          resolve(id);
        },
        onlauncherror: function(id, error) {
          logErr('launchApp(' + id + ')', error);
          const msg = errText(error);
          // WAS_ERROR_LAUNCH_NOT_EXIST heisst: die App ist auf diesem Geraet
          // nicht vorhanden. Das ist die Auskunft, auf die man sich verlassen
          // kann - anders als auf getCapability, dessen Werte Samsung nicht
          // oeffentlich dokumentiert. Am Geraet gemessen: getCapability lieferte
          // "CAPABILITY_ENABLE", waehrend der Start mit LAUNCH_NOT_EXIST
          // scheiterte; die Vorpruefung auf CAPABILITY_UNINSTALL griff also
          // nicht. Deshalb hier nach dem TATSAECHLICHEN Fehler nachinstallieren
          // und genau einmal erneut starten.
          if (!retried && msg.indexOf('NOT_EXIST') !== -1 && typeof w.installApp === 'function') {
            retried = true;
            console.log('[Cast/Tizen] ' + id + ' ist nicht installiert - wird nachinstalliert');
            CastTizen.installApp(appId, opts.onProgress).then(doLaunch, failed);
            return;
          }
          failed(error || new Error('launch error'));
        },
      };
      const doLaunch = () => {
        try {
          // Zweites Argument ist das Metadatum, das die gestartete App bekommt.
          // Fuer die OTT-Apps leer wie bisher; der Geraete-Browser braucht dort
          // seine Zieladresse (siehe helpers/Browser.js).
          w.launchApp(appId, opts.metaData || '', listener);
        } catch (e) {
          logErr('launchApp(' + appId + ')', e);
          failed(e);
        }
      };
      // Bei UNINSTALL-Capability zuerst installieren, dann starten.
      const cap = opts.checkCapability ? CastTizen.getCapability(appId) : null;
      if (cap === 'CAPABILITY_UNINSTALL') {
        CastTizen.installApp(appId, opts.onProgress).then(doLaunch, failed);
      } else {
        doLaunch();
      }
    });
  }

  // =========================================================================
  // Google Cast
  // =========================================================================

  static gcInitialize() {
    const gc = googlecast();
    if (!gc) return false;
    try {
      gc.initialize();
      return true;
    } catch (e) {
      logErr('gcInitialize', e);
      return false;
    }
  }

  static gcFinalize() {
    const gc = googlecast();
    if (!gc) return false;
    try {
      gc.finalize();
      return true;
    } catch (e) {
      logErr('gcFinalize', e);
      return false;
    }
  }

  // initialize ohne finalize - wie Samsungs Vorlage (Hotel-SI-Checkliste,
  // API-TC 3/4). finalize gehoert nur zum Checkout (finalizeForCheckOut):
  // mitten in einer Sitzung, etwa beim Info-Abruf, koennte es den laufenden
  // Cast des Gastes beenden.
  // Tizen < 7: nach jedem Aufruf finalize (siehe GC_FINALIZE_BELOW_TIZEN).
  // Unbekannte Version: wie Samsungs Vorlage, also ohne.
  static gcNeedsFinalize() {
    const major = parseInt(CastTizen.getPlatformVersion(), 10);
    return !isNaN(major) && major < GC_FINALIZE_BELOW_TIZEN;
  }

  static _gcClose(gc) {
    if (!CastTizen.gcNeedsFinalize()) return;
    try {
      gc.finalize();
    } catch (e) {
      logErr('gcFinalize', e);
    }
  }

  static gcGetStatus() {
    const gc = googlecast();
    if (!gc) return null;
    try {
      gc.initialize();
      const status = gc.getStatus();
      CastTizen._gcClose(gc);
      return status;
    } catch (e) {
      logErr('gcGetStatus', e);
      return null;
    }
  }

  // MIT finalize - anders als Status und launchSettings.
  //
  // So lief es auf dem BU800 (Tizen 6.5) erprobt. Ohne das finalize
  // (6dce574, nach Samsungs Vorlage) blieb Google Cast dort dauerhaft auf
  // INIT, auch nach einem Stromlos-Neustart: das Handy fand den TV nicht,
  // vorher meldete es "YouTube nicht installiert". Am U800F (Tizen 9) stand
  // derselbe Ablauf auf READY. Die ToS-Annahme laeuft nur beim Vorbereiten
  // (cast/prepare) und beim App-Start - nie mitten in einem Cast.
  static gcAcceptTermsOfService() {
    const gc = googlecast();
    if (!gc) return false;
    try {
      gc.initialize();
      gc.acceptTermsOfService();
      gc.finalize();
      return true;
    } catch (e) {
      logErr('gcAcceptTermsOfService', e);
      return false;
    }
  }

  // Beendet einen laufenden Cast, wenn die tv-app wieder in den Vordergrund
  // kommt.
  //
  // Am BU800 lief nach HOME der Ton des vom Handy gestarteten YouTube weiter -
  // ueber com.samsung.tv.googlecast-app im Hintergrund. Ein Stop/Terminate gibt
  // es in webapis.googlecast nicht; finalize ist der einzige Hebel und genau
  // der Aufruf, vor dem Samsungs Vorlage warnt ("koennte einen laufenden Cast
  // beenden") - hier ist das gewollt. VERSUCH: ob finalize den Cast wirklich
  // beendet, ist am Geraet noch nicht belegt; das Log zeigt den Status davor
  // und danach. Liefert den Status vor dem Aufruf (oder null).
  static gcStopIfCasting() {
    const gc = googlecast();
    if (!gc) return null;
    let status = null;
    try {
      gc.initialize();
      status = gc.getStatus();
      if (status !== 'CASTING') {
        CastTizen._gcClose(gc);
        return status;
      }
      gc.finalize();
      let after = null;
      try {
        gc.initialize();
        after = gc.getStatus();
        CastTizen._gcClose(gc);
      } catch (e) {
        /* Status danach unbekannt */
      }
      console.log('[Cast/Tizen] tv-app wieder vorn, Google Cast lief - finalize gerufen, Status danach: ' + after);
    } catch (e) {
      logErr('gcStopIfCasting', e);
    }
    return status;
  }

  // Loescht die Daten des Google-Cast-Dienstes (webapis.googlecast.clearData).
  //
  // Betrifft NUR Google Cast selbst - gekoppelte Geraete und Einstellungen des
  // Dienstes -, nicht den Speicher der tv-app (bid, Registrierung, localStorage)
  // und keine anderen Apps. Was genau darunter faellt, dokumentiert Samsung
  // nicht. Weil es vermutlich auch die Annahme der Nutzungsbedingungen
  // zuruecksetzt, werden sie danach erneut akzeptiert - sonst waere der TV fuer
  // den naechsten Gast kein Cast-Ziel mehr. Promise<boolean>, nie reject.
  static gcClearData() {
    const gc = googlecast();
    if (!gc || typeof gc.clearData !== 'function') return Promise.resolve(false);
    try {
      gc.initialize();
      gc.clearData();
      gc.finalize();
      console.log('[Cast/Tizen] Google-Cast-Daten geloescht (clearData)');
    } catch (e) {
      logErr('gcClearData', e);
      return Promise.resolve(false);
    }
    return CastTizen.gcAcceptTermsWhenReady().then(() => true, () => true);
  }

  // Das Usage-Bit ("Help us improve", eureka_info opt_in.stats) - NUR lesen.
  //
  // Samsungs Hotel-SI-Checkliste (DeviceTC 3/5-8) verlangt: nach dem Checkout
  // false, und erst die Firmware stellt es auf true, wenn der Gast die
  // Pairing-Seite bzw. die Cast-Einstellungen oeffnet. Die App schaltet es
  // deshalb NIE selbst ein - ein frueheres gcEnableUsageReporting ist entfernt.
  // Liefert den Wert der Firmware ('DEFAULT', 'ENABLED', ...) oder null.
  static gcUsageReportingStatus() {
    const gc = googlecast();
    if (!gc || typeof gc.getUsageReportingStatus !== 'function') return null;
    try {
      gc.initialize();
      const usage = String(gc.getUsageReportingStatus());
      CastTizen._gcClose(gc);
      return usage;
    } catch (e) {
      logErr('gcUsageReportingStatus', e);
      return null;
    }
  }

  // Wartet, bis Google Cast nicht mehr in 'INIT' steht.
  //
  // So macht es Samsungs Vorlage (Hotel-SI-Checkliste, API-TC 5/6): steht der
  // Dienst noch auf INIT - typisch kurz nach dem Hochlaufen, also gerade nach
  // dem Checkout-Neustart -, erst registerStatusChangeListener und den
  // naechsten Schritt im Listener. Loest mit dem Status auf ('READY',
  // 'CASTING', nach Ablauf der Wartezeit auch noch 'INIT'), bei Fehlern mit
  // null. Nie reject.
  static gcWhenReady(timeoutMs) {
    const gc = googlecast();
    if (!gc) return Promise.resolve(null);
    let status;
    try {
      gc.initialize();
      status = gc.getStatus();
    } catch (e) {
      logErr('gcWhenReady', e);
      return Promise.resolve(null);
    }
    if (status !== 'INIT') {
      CastTizen._gcClose(gc);
      return Promise.resolve(status);
    }
    console.log('[Cast/Tizen] Google Cast steht noch auf INIT - warte auf den Statuswechsel');
    return new Promise((resolve) => {
      let done = false;
      let listening = false;
      const finish = (s) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        if (listening) {
          try {
            gc.unregisterStatusChangeListener();
          } catch (e) {
            /* dann eben nicht */
          }
        }
        CastTizen._gcClose(gc);
        resolve(s);
      };
      const timer = setTimeout(() => {
        let s = null;
        try {
          s = gc.getStatus();
        } catch (e) {
          /* letzter Stand unbekannt */
        }
        console.log('[Cast/Tizen] Google Cast nach ' + (timeoutMs || GC_READY_TIMEOUT_MS) + ' ms: ' + s);
        finish(s);
      }, timeoutMs || GC_READY_TIMEOUT_MS);
      try {
        if (typeof gc.registerStatusChangeListener === 'function') {
          gc.registerStatusChangeListener((s) => {
            console.log('[Cast/Tizen] Google Cast Status -> ' + s);
            if (s !== 'INIT') finish(s);
          });
          listening = true;
        }
      } catch (e) {
        logErr('registerStatusChangeListener', e);
      }
    });
  }

  // Oeffnet die native Google-Cast-Settings-Seite.
  //
  // args: 'ShowInstructions:false,HideButtons:false' fuer das Guest-paired
  // Network (SoftAP + QR, API-TC 5 - unser Weg), '' fuer ein
  // vorkonfiguriertes Zimmer-WLAN (API-TC 6). Wartet vorher auf das Ende von
  // INIT und ruft danach - wie die Vorlage - KEIN finalize auf: die Seite soll
  // offen bleiben. Liefert ein Promise<boolean>.
  static gcLaunchSettings(args) {
    const gc = googlecast();
    if (!gc) return Promise.resolve(false);
    return CastTizen.gcWhenReady().then((status) => {
      if (status === 'INIT') console.log('[Cast/Tizen] Google Cast noch in INIT - Einstellungen trotzdem oeffnen');
      try {
        // Auf Tizen < 7 hat gcWhenReady schon geschlossen - neu oeffnen und
        // danach wieder schliessen, wie bis 6dce574.
        const balanced = CastTizen.gcNeedsFinalize();
        if (balanced) gc.initialize();
        gc.launchSettings(typeof args === 'string' ? args : 'ShowInstructions:false,HideButtons:false');
        if (balanced) CastTizen._gcClose(gc);
        return true;
      } catch (e) {
        logErr('gcLaunchSettings', e);
        return false;
      }
    });
  }

  // ToS beim App-Start akzeptieren (Checkliste DeviceTC 8: nach Checkout und
  // neuem Check-in "ready to cast", ToS bereits akzeptiert). Bisher geschah
  // das erst beim Oeffnen der Cast-Seite (cast/prepare). Hier OHNE Token und
  // ohne den prepared-Merker - das Token setzt weiterhin cast/prepare mit dem
  // Wert des Geraets. Wartet auf das Ende von INIT. Promise<boolean>.
  static gcAcceptTermsWhenReady() {
    if (!googlecast()) return Promise.resolve(false);
    return CastTizen.gcWhenReady().then((status) => {
      const ok = CastTizen.gcAcceptTermsOfService();
      console.log('[Cast/Tizen] ToS beim Start akzeptiert: ' + ok + ' (Status ' + status + ')');
      return ok;
    });
  }

  // =========================================================================
  // SoftAP (Hotspot des TV) -> Grundlage fuer den WLAN-QR
  // =========================================================================

  // Schreibt die SSID des SoftAP.
  //
  // Zum '[TV]'-Praefix, das ein BU800 (Tizen 6.5) vor dem gesendeten Namen
  // zeigt: es kommt NICHT von hier, geschrieben wird genau der uebergebene
  // String. Zwei Erklaerungen sind moeglich, und sie lassen sich am Log
  // unterscheiden:
  //   1. Die Firmware stellt es selbst voran (Samsungs Wi-Fi-Direct-Konvention
  //      '[TV]<Geraetename>'). Dann steht im Log die Zeile aus verifySoftAP:
  //      'SoftAP-SSID weicht ab: gesetzt "Zi399-T8UE", gelesen
  //      "[TV]Zi399-T8UE"'.
  //   2. setSoftAPSSID gibt es auf dieser Firmware gar nicht - dann bleibt der
  //      Name, den der TV selbst vergeben hat, und das ist ebenfalls
  //      '[TV]<Geraetename>'. Diesen Fall meldet die Zeile darunter.
  // Welcher es ist, sagt zusaetzlich die Zeile 'SoftAP-API des Geraets' auf
  // /info: steht setSoftAPSSID dort nicht, ist es Fall 2.
  // Schneidet ein Praefix ab, das die Firmware dem Netznamen voranstellt.
  //
  // Belegt an einem BU800 (Tizen 6.5): geschrieben 'devo-WUN9', gesendet und
  // zurueckgelesen '[TV]devo-WUN9'. setSoftAPSSID existiert dort und hat
  // funktioniert - die Firmware stellt das '[TV]' selbst voran, Samsungs
  // Wi-Fi-Direct-Konvention. Ein U800F (Tizen 9.0) tut das nicht. In der
  // gesamten webapis.network-Liste beider Geraete gibt es nichts, womit sich
  // das abschalten liesse.
  //
  // Fuer den QR und die Anzeige gilt weiterhin der ZURUECKGELESENE Name - der
  // Gast sucht genau den in seiner WLAN-Liste. Diese Funktion dient nur dem
  // Vergleich "steht schon das Richtige auf dem Geraet", damit nicht bei jedem
  // Oeffnen der Cast-Seite neu geschrieben und der Hotspot neu gestartet wird.
  static stripSsidPrefix(ssid) {
    return String(ssid == null ? '' : ssid).replace(/^\[[^\]]{1,12}\]\s*/, '');
  }

  static setSoftAPSSID(ssid) {
    const n = network();
    if (!n || typeof n.setSoftAPSSID !== 'function') {
      console.log('[Cast/Tizen] setSoftAPSSID gibt es auf dieser Firmware NICHT - die SSID bleibt, wie der TV sie vergibt');
      return false;
    }
    try {
      n.setSoftAPSSID(ssid);
      return true;
    } catch (e) {
      logErr('setSoftAPSSID', e);
      return false;
    }
  }

  // Schreibt einen neuen Schluessel, OHNE den Hotspot anzufassen.
  //
  // Fuer Geraete, die ihren SoftAP nicht neu starten koennen: dort wird ein
  // geschriebener Schluessel erst mit einem Neustart des TV wirksam. Genau das
  // ist der Sinn dieser Funktion - sie wird unmittelbar VOR einem Neustart
  // gerufen, den wir selbst ausloesen. Angezeigt wird der neue Schluessel erst
  // nach dem Neustart (siehe cast/settleAfterRebootClear); bis dahin sendet der
  // Hotspot mit dem alten, und die Cast-Seite zeigt genau den.
  static stageSoftApKey(key) {
    const ok = CastTizen.setSoftAPSecurityKey(key);
    console.log('[Cast/Tizen] SoftAP-Schluessel fuer den Neustart vorbereitet:', ok ? 'geschrieben' : 'fehlgeschlagen');
    return ok;
  }

  static setSoftAPSecurityKey(key) {
    const n = network();
    if (!n || typeof n.setSoftAPSecurityKey !== 'function') {
      console.log('[Cast/Tizen] setSoftAPSecurityKey gibt es auf dieser Firmware NICHT - das Passwort bleibt unveraendert');
      return false;
    }
    try {
      n.setSoftAPSecurityKey(key);
      return true;
    } catch (e) {
      logErr('setSoftAPSecurityKey', e);
      return false;
    }
  }

  // Traegt der TV-Name schon unseren Namen, nur mit Firmware-Praefix?
  //
  // getTVName liefert auf dem BU800 '[TV]devo-WUN9'. Der Vergleich in
  // cast/applyDeviceName sah darin eine Abweichung, schrieb bei JEDEM Start
  // erneut - und setTVName scheitert dort ("Geraetename ... fehlgeschlagen").
  static tvNameMatches(wanted) {
    const current = CastTizen.getTVName();
    if (!current || !wanted) return false;
    return current === wanted || CastTizen.stripSsidPrefix(current) === wanted;
  }

  // Setzt den Geraetenamen des TV (webapis.network.setTVName).
  //
  // Bis 07.10.2026 gab es diese Methode in CastTizen gar nicht - cast/
  // applyDeviceName meldete auf Samsung deshalb bei jeder Abweichung
  // "Geraetename ... fehlgeschlagen" (TestHeikoDavid0705), ohne dass je
  // geschrieben wurde. Die Firmware fuehrt setTVName in webapis.network.
  // Ob der Name sofort oder erst nach einem Neustart gilt, und ob Google Cast
  // ihn uebernimmt, zeigt der Rueckleseversuch im Log. Promise<boolean>.
  static setTVName(name) {
    const n = network();
    if (!n || typeof n.setTVName !== 'function' || !name) return Promise.resolve(false);
    try {
      n.setTVName(name);
    } catch (e) {
      logErr('setTVName', e);
      return Promise.resolve(false);
    }
    const back = CastTizen.getTVName();
    console.log('[Cast/Tizen] setTVName("' + name + '") - zurueckgelesen: "' + back + '"');
    return Promise.resolve(true);
  }

  static getTVName() {
    const n = network();
    if (!n || typeof n.getTVName !== 'function') return null;
    try {
      return n.getTVName();
    } catch (e) {
      logErr('getTVName', e);
      return null;
    }
  }

  // Versucht, die aktuell aktive SoftAP-SSID bzw. den Key vom TV zu lesen.
  // Samsung dokumentiert dafuer keine feste Getter-API - je nach Firmware
  // existieren unterschiedliche Namen, daher defensiv mehrere probieren.
  static getSoftAPSSID() {
    const n = network();
    if (!n) return null;
    const fns = ['getSoftAPSSID', 'getSoftApSSID', 'getSoftAPSsid'];
    for (let i = 0; i < fns.length; i++) {
      if (typeof n[fns[i]] === 'function') {
        try {
          const v = n[fns[i]]();
          if (v) return v;
        } catch (e) {
          /* naechsten Namen versuchen */
        }
      }
    }
    return null;
  }

  static getSoftAPSecurityKey() {
    const n = network();
    if (!n) return null;
    const fns = ['getSoftAPSecurityKey', 'getSoftApSecurityKey', 'getSoftAPKey'];
    for (let i = 0; i < fns.length; i++) {
      if (typeof n[fns[i]] === 'function') {
        try {
          const v = n[fns[i]]();
          if (v) return v;
        } catch (e) {
          /* naechsten Namen versuchen */
        }
      }
    }
    return null;
  }

  // Erzeugt einmalig ein SoftAP-Passwort (ohne verwechselbare Zeichen) und
  // merkt es sich fuer die Laufzeit, damit wiederholtes Oeffnen der Cast-Seite
  // nicht bei jedem Mal ein neues Passwort setzt (QR wuerde sonst veralten).
  static _softApPassword(force) {
    if (CastTizen._genKey && !force) return CastTizen._genKey;
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let s = '';
    for (let i = 0; i < 8; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
    CastTizen._genKey = s;
    return s;
  }

  // Welche SoftAP-Funktionen kennt dieses Geraet ueberhaupt?
  //
  // Samsung dokumentiert die TEP-Netzwerk-APIs nicht oeffentlich und die Namen
  // weichen je Firmware ab. Ohne diese Liste laesst sich am realen TV nicht
  // feststellen, warum ein Schreibzugriff wirkungslos bleibt - deshalb wird sie
  // einmal geloggt und auf der Diagnose-Seite angezeigt.
  // ALLE Funktionen von webapis.network - nicht nur die mit "softap" im Namen.
  //
  // Der Grund: auf einem U800F (Tizen 9.0) sind addSoftAPClientInfoListener und
  // addSoftAPStateChangeListener beide registrierbar und beide stumm, auch bei
  // einer frisch aufgebauten Verbindung, und eine lesende SoftAP-Client-Abfrage
  // gibt es nicht. Wenn dieses Geraet ueberhaupt Auskunft ueber verbundene
  // Geraete gibt, dann unter einem Namen, der nicht "softap" enthaelt - etwa
  // ueber DHCP-Leases. Statt zu raten wird die vollstaendige Liste auf /info
  // gezeigt.
  // Alle Funktionsnamen eines webapis-Objekts, sortiert.
  static apiNames(obj) {
    if (!obj) return [];
    const out = [];
    for (const k in obj) {
      try {
        if (typeof obj[k] === 'function') out.push(k);
      } catch (e) {
        /* manche Properties werfen beim Zugriff - dann eben ohne */
      }
    }
    return out.sort();
  }

  static networkApiNames() {
    const n = network();
    if (!n) return [];
    const out = [];
    for (const k in n) {
      try {
        if (typeof n[k] === 'function') out.push(k);
      } catch (e) {
        /* manche Properties werfen beim Zugriff - dann eben ohne */
      }
    }
    return out.sort();
  }

  // Alle Funktionsnamen von webapis.clone.
  //
  // GEMESSEN, September 2026, auf zwei Generationen - BU800 (Tizen 6.x/7.x)
  // und U800F (Tizen 9.0) - jeweils identisch:
  //
  //   getVersion, restoreCloneData,
  //   setCloneUpdateStatusListener, unSetCloneUpdateStatusListener
  //
  // Also NUR anwenden. Kein store/save/get/export: ein Clonefile laesst sich
  // vom Geraet nicht auslesen, weder gesichert noch ungesichert. Dass die
  // Aufzaehlung dabei wirklich lief und nicht in den Rueckfall unten geriet,
  // beweist 'getVersion' - der Name steht in CLONE_API_GUESSES nicht drin.
  //
  // Auf LG ist es dasselbe Bild, dort aber schon aus der mitgelieferten
  // hcap.js ablesbar: hcap.system kennt nur requestCloning (anwenden), und
  // hcap.file kann nur USB (downloadFileToUsb geht Netz -> USB).
  //
  // Die Sonde bleibt trotzdem: kommt eine kuenftige Firmware mit einer
  // Gegenrichtung, steht sie ohne weiteres Zutun auf /info.
  //
  // Leer bei vorhandener API heisst wirklich leer - ob die API ueberhaupt da
  // ist, sagt hasClone daneben. Ohne diese Trennung waere "keine Funktionen
  // gefunden" nicht von "gibt es auf diesem Geraet nicht" zu unterscheiden,
  // und genau diese Unterscheidung ist hier die Frage.
  static cloneApiNames() {
    const c = clone();
    if (!c) return [];
    const out = [];
    for (const k in c) {
      try {
        if (typeof c[k] === 'function') out.push(k);
      } catch (e) {
        /* manche Properties werfen beim Zugriff - dann eben ohne */
      }
    }
    // Manche WebIDL-Objekte legen ihre Methoden nur auf dem Prototyp ab und
    // sind mit for..in nicht zu erfassen. Dann wenigstens gezielt nachfragen.
    if (out.length === 0) {
      CLONE_API_GUESSES.forEach((name) => {
        try {
          if (typeof c[name] === 'function') out.push(name);
        } catch (e) {
          /* nicht vorhanden */
        }
      });
    }
    return out.sort();
  }

  static softApApiNames() {
    const n = network();
    if (!n) return [];
    const out = [];
    for (const k in n) {
      try {
        if (/softap/i.test(k) && typeof n[k] === 'function') out.push(k);
      } catch (e) {
        /* manche Properties werfen beim Zugriff - dann eben ohne */
      }
    }
    return out.sort();
  }

  // Findet die Funktionen, mit denen sich der SoftAP aus- und einschalten laesst.
  //
  // Liefert IMMER ein Paar oder null. Das ist der ganze Sinn dieser Funktion:
  // wer den SoftAP ausschaltet, muss ihn auch wieder einschalten koennen. Fehlt
  // die Gegenrichtung, bleibt der Hotspot aus und muss am TV ueber das
  // Service-Menue wieder aktiviert werden - genau das darf nicht passieren.
  //
  // Bewusst NICHT in der Liste: 'setSoftAP'. Der Name existiert auf manchen
  // Firmwares mit voellig anderer Signatur (Konfigurationsobjekt statt Flag);
  // ein Aufruf mit 0/1 schaltet den Hotspot dann ab, ohne ihn zurueckholen zu
  // koennen. Samsung dokumentiert die TEP-Netzwerk-APIs nicht oeffentlich,
  // deshalb wird hier nur benutzt, was vom Namen her eindeutig ist.
  static _softApSwitch() {
    const n = network();
    if (!n) return null;
    // Variante A: ein Setter mit Flag - eine Funktion, beide Richtungen.
    const setters = ['setSoftAPEnable', 'setSoftAPEnabled', 'setSoftApEnable'];
    for (let i = 0; i < setters.length; i++) {
      if (typeof n[setters[i]] === 'function') {
        const name = setters[i];
        return {
          name: name,
          off: () => n[name](0),
          on: () => n[name](1),
        };
      }
    }
    // Variante B: zwei getrennte Funktionen - nur wenn BEIDE existieren.
    const pairs = [['enableSoftAP', 'disableSoftAP'], ['startSoftAP', 'stopSoftAP']];
    for (let i = 0; i < pairs.length; i++) {
      const onName = pairs[i][0];
      const offName = pairs[i][1];
      if (typeof n[onName] === 'function' && typeof n[offName] === 'function') {
        return {
          name: onName + '/' + offName,
          off: () => n[offName](),
          on: () => n[onName](),
        };
      }
    }
    return null;
  }

  // Startet den SoftAP neu, damit eine neue SSID bzw. ein neues Passwort
  // WIRKLICH gilt.
  //
  // Der Grund: ein laufender Access Point uebernimmt eine geaenderte
  // Konfiguration nicht im Betrieb, er sendet weiter mit den alten Zugangsdaten.
  // Angezeigtes Passwort und QR waeren dann korrekt gemerkt, das Handy kaeme
  // aber trotzdem nicht ins Netz. Auf LG steht das ausdruecklich in der
  // HCAP-Doku (network.setSoftAP: wirksam erst beim naechsten Boot oder wenn
  // soft_ap auf 1 wechselt); auf Samsung ist es nicht dokumentiert.
  //
  // Kennt das Geraet kein eindeutiges Schaltfunktions-Paar, wird NICHTS
  // geschaltet. Ein halber Neustart waere schlimmer als keiner: der Hotspot
  // bliebe aus und muesste am TV von Hand wieder aktiviert werden.
  static restartSoftAP() {
    const sw = CastTizen._softApSwitch();
    if (!sw) {
      console.log(
        '[Cast/Tizen] SoftAP-Neustart uebersprungen - kein eindeutiges Schaltfunktions-Paar. ' +
          'Vorhandene SoftAP-Funktionen: ' + (CastTizen.softApApiNames().join(', ') || 'keine')
      );
      return { ok: false, tried: false };
    }
    // Hat das Einschalten auf diesem Geraet je versagt, wird NICHT MEHR
    // geschaltet - auch nicht ausgeschaltet.
    //
    // Die Regel im Kopf dieser Datei ("wer ausschaltet, muss auch wieder
    // einschalten koennen") war richtig, nur haben wir sie an der Existenz der
    // Funktion gemessen statt an ihrem Erfolg. Am Geraet lief der Hotspot,
    // disableSoftAP ging durch, enableSoftAP scheiterte - und er war weg.
    if (CastTizen.isSoftApSwitchBroken()) {
      console.log('[Cast/Tizen] SoftAP-Neustart uebersprungen - dieses Geraet bekommt ihn nicht wieder an');
      return { ok: false, tried: false };
    }
    // Und wenn es noch nie geprueft wurde: JETZT pruefen, solange der Hotspot
    // noch laeuft. Erst danach darf ausgeschaltet werden.
    if (CastTizen.isSoftApEnabled() === true && !CastTizen.probeSoftApSwitch()) {
      console.log('[Cast/Tizen] SoftAP-Neustart abgebrochen - Einschalten funktioniert auf diesem Geraet nicht');
      return { ok: false, tried: false };
    }

    // Liegt der Hotspot schon, NICHT erst ausschalten. Jeder off/on-Zyklus ist
    // eine Gelegenheit, ihn ganz zu verlieren - genau so ist er am Geraet
    // steckengeblieben (isSoftAPEnabled false, getSoftAPSignalStrength
    // 0xFFFFFFFF, also -1: der Treiber ist unten, nicht bloss abgeschaltet).
    if (CastTizen.isSoftApEnabled() !== false) {
      // Das Einschalten MUSS auch dann laufen, wenn das Ausschalten wirft -
      // sonst bleibt der Hotspot aus.
      try {
        sw.off();
      } catch (e) {
        logErr('softAP off (' + sw.name + ')', e);
      }
    } else {
      console.log('[Cast/Tizen] SoftAP ist bereits aus - nur einschalten');
    }
    try {
      sw.on();
    } catch (e) {
      logErr('softAP on (' + sw.name + ')', e);
      // HIER dauerhaft merken. Das war der Fehler: markSoftApSwitchBroken stand
      // nur in ensureSoftApEnabled, der Fehlschlag passiert aber GENAU HIER -
      // im on() nach dem off() der Rotation. Der Merker wurde also nie gesetzt,
      // und beim naechsten Oeffnen der Cast-Seite schaltete die Rotation den
      // Hotspot wieder ab.
      if (!isSecurityError(e)) CastTizen.markSoftApSwitchBroken();
      // tried: das Einschalten IST gelaufen, es hat nur nicht geklappt. Der
      // Aufrufer darf es dann nicht gleich wiederholen: enableSoftAP braucht auf
      // dieser Firmware rund zehn Sekunden, bis es mit "Failed to enable Soft AP"
      // aufgibt - zweimal hintereinander waren das gemessene 17 s, die
      // vollstaendig in der Wartezeit des Gastes landeten.
      return { ok: false, tried: true };
    }
    console.log('[Cast/Tizen] SoftAP neu gestartet (' + sw.name + ')');
    return { ok: true, tried: true };
  }

  // Stellt sicher, dass der SoftAP laeuft - ohne ihn vorher auszuschalten.
  //
  // Gegenstueck zu restartSoftAP fuer den Normalfall: der Hotspot ist im
  // Service-Menue eingeschaltet und soll es bleiben. Sollte er trotzdem aus sein
  // (Standby, Firmware-Eigenheit, frueherer Fehlversuch), holt das ihn zurueck,
  // ohne je in die Gegenrichtung zu schalten.
  static ensureSoftApEnabled() {
    const sw = CastTizen._softApSwitch();
    if (!sw) return false;
    try {
      sw.on();
      return true;
    } catch (e) {
      logErr('softAP on (' + sw.name + ')', e);
      if (isSecurityError(e)) {
        console.log('[Cast/Tizen] SoftAP: keine Berechtigung (SecurityError) - WGT/Zertifikat pruefen');
        return false;
      }
      // Dauerhaft merken: ab jetzt wird auf diesem Geraet nicht mehr
      // geschaltet, weder aus noch ein.
      CastTizen.markSoftApSwitchBroken();
      return false;
    }
  }

  // Schaltet den SoftAP AUS - und sonst nichts.
  //
  // Der einzige SoftAP-Aufruf, den der Ausschaltpfad noch verantworten kann:
  // restartSoftAP() haelt mit off+on den JS-Thread mehrere Sekunden und
  // verzoegerte damit das powerOff des TV. Eingeschaltet wird stattdessen beim
  // Aufwachen (ensureSoftApEnabled bzw. rotateSoftAP) - und genau dieser
  // Uebergang 0 -> 1 ist es, der eine neue Konfiguration wirksam macht.
  //
  // Nur bei einem eindeutigen Schaltfunktions-PAAR: wer ausschaltet, muss auch
  // wieder einschalten koennen (siehe _softApSwitch).
  //
  // WIRD IM AUSSCHALTPFAD NICHT MEHR BENUTZT. Die Voraussetzung "wir koennen ihn
  // wieder einschalten" hat sich am Geraet als falsch erwiesen: enableSoftAP
  // scheitert dort mit "Failed to enable Soft AP", und der Hotspot blieb ueber
  // mehrere Power-Zyklen aus. Ausgeschaltet wird er deshalb nur noch dort, wo
  // unmittelbar danach wieder eingeschaltet und das Ergebnis geprueft wird
  // (restartSoftAP + ensureSoftApUp in rotateSoftAP).
  static disableSoftAP() {
    const sw = CastTizen._softApSwitch();
    if (!sw) return false;
    try {
      sw.off();
      console.log('[Cast/Tizen] SoftAP fuer den Standby abgeschaltet (' + sw.name + ')');
      return true;
    } catch (e) {
      logErr('softAP off (' + sw.name + ')', e);
      return false;
    }
  }

  // Ruft alle lesenden SoftAP-Funktionen des Geraets auf und liefert Name ->
  // Wert.
  //
  // Der Weg ueber geratene Namen ist gescheitert: das Geraet kennt zwar viele
  // SoftAP-Funktionen, aber keine mit einem der erwarteten Namen. Statt weiter
  // zu raten wird hier aufgezaehlt, was da ist, und ausprobiert, was antwortet.
  //
  // Nur Namen, die nach einem Getter aussehen (get/is/has) und ohne Argument
  // aufgerufen werden koennen. Das ist die uebliche Konvention der TEP-APIs;
  // alles andere bleibt unangetastet, denn ein Fehlgriff auf der Schreibseite
  // hat schon einmal den Hotspot abgeschaltet.
  // Und dasselbe Wissen DAUERHAFT: dieses Geraet kann seinen SoftAP nicht
  // wieder einschalten.
  //
  // Am Geraet belegt: der Hotspot LIEF (isSoftAPEnabled true), rotateSoftAP
  // schrieb das neue Passwort und rief restartSoftAP - disableSoftAP ging
  // durch, enableSoftAP scheiterte zehn Sekunden spaeter mit
  // "[9999] Failed to enable Soft AP", und der Hotspot war weg. Unsere eigene
  // Rotation hat ihn also zerstoert, und zwar zuverlaessig bei jedem Oeffnen
  // der Cast-Seite nach einem Standby.
  //
  // Ein Merker nur fuer die Laufzeit reicht dagegen nicht: der erste Versuch
  // nach jedem App-Start haette den Hotspot erneut umgebracht. Deshalb im
  // localStorage.
  static _switchBrokenKey = 'cast_softap_switch_broken';

  static isSoftApSwitchBroken() {
    try {
      return !!(window.localStorage && window.localStorage.getItem(CastTizen._switchBrokenKey) === '1');
    } catch (e) {
      return false;
    }
  }

  // Prueft, OB dieses Geraet seinen Hotspot einschalten kann - ohne ihn dafuer
  // auszuschalten.
  //
  // Das ist der Kern der Lehre aus zwei kaputten Hotspots: bisher haben wir es
  // erst HINTERHER erfahren. Die Rotation schaltete aus, und zehn Sekunden
  // spaeter zeigte das gescheiterte Einschalten, dass es nicht geht - da war
  // der Hotspot schon weg.
  //
  // Der Aufruf geht bewusst auf einen LAUFENDEN Hotspot. enableSoftAP kann ihn
  // dabei nicht abschalten; im guenstigsten Fall ist es ein No-op, im
  // schlechtesten wirft es - und dann wissen wir es, bevor Schaden entsteht.
  //
  // Antwortet die Firmware auch hier erst nach zehn Sekunden, kostet das einmal
  // Zeit auf der Cast-Seite (wo eine Wartezeit angezeigt wird) und danach nie
  // wieder: das Ergebnis liegt persistent im Merker.
  static probeSoftApSwitch() {
    if (CastTizen.isSoftApSwitchBroken()) return false;
    const sw = CastTizen._softApSwitch();
    if (!sw) return false;
    // Nur auf einem laufenden Hotspot pruefen. Liegt er, ist die Frage ohnehin
    // beantwortet - dann hilft nur ein Neustart des TV.
    if (CastTizen.isSoftApEnabled() !== true) return false;
    try {
      sw.on();
      console.log('[Cast/Tizen] SoftAP-Schalter geprueft: Einschalten funktioniert');
      return true;
    } catch (e) {
      logErr('softAP on (Probe)', e);
      if (!isSecurityError(e)) CastTizen.markSoftApSwitchBroken();
      return false;
    }
  }

  // Heisst ausdruecklich "nicht NEUSTARTBAR", nicht "nicht einschaltbar":
  // gesperrt werden damit nur das Ausschalten und die Rotation. Einschalten
  // wird weiter versucht.
  static markSoftApSwitchBroken() {
    try {
      if (window.localStorage) window.localStorage.setItem(CastTizen._switchBrokenKey, '1');
    } catch (e) {
      /* dann gilt nur der Laufzeit-Merker */
    }
    console.log('[Cast/Tizen] Dieses Geraet kann den SoftAP nicht wieder einschalten - wird nie mehr geschaltet');
  }

  static softApProbe() {
    const n = network();
    if (!n) return [];
    const names = CastTizen.softApApiNames().filter((k) => /^(get|is|has)/.test(k));
    const out = [];
    for (let i = 0; i < names.length; i++) {
      const name = names[i];
      let value;
      try {
        value = n[name]();
      } catch (e) {
        const msg = (e && (e.message || e.name)) || String(e);
        out.push({ name: name, value: null, error: msg, text: 'Fehler: ' + msg });
        continue;
      }
      let text;
      if (value === null || typeof value === 'undefined') {
        text = String(value);
      } else if (typeof value === 'object') {
        try {
          text = JSON.stringify(value);
        } catch (e) {
          text = String(value);
        }
      } else {
        text = String(value);
      }
      out.push({ name: name, value: value, text: text.length > 200 ? text.slice(0, 200) + '…' : text });
    }
    return out;
  }

  // Namen der Funktionen, die nach einer Client-Abfrage aussehen.
  //
  // Einmal ermittelt, denn die Namen aendern sich zur Laufzeit nicht - die WERTE
  // aber sehr wohl, und genau darum wird hier nur der Name gemerkt und nicht das
  // Ergebnis. Ein bei der Erkennung gemerktes "gibt nichts her" waere fatal:
  // zu dem Zeitpunkt hat sich noch kein Handy verbunden.
  static softApClientFnNames() {
    if (!CastTizen._clientFns) {
      CastTizen._clientFns = CastTizen.softApApiNames().filter(
        (k) => /^(get|is|has)/.test(k) && /client|device|station|connect|num|count|list/i.test(k)
      );
      console.log(
        '[Cast/Tizen] SoftAP-Client-Kandidaten: ' + (CastTizen._clientFns.join(', ') || 'keine')
      );
    }
    return CastTizen._clientFns;
  }

  // Fragt den IST-Stand der am SoftAP verbundenen Geraete ab.
  //
  // Notwendig, weil addSoftAPStateChangeListener nur AENDERUNGEN meldet: war das
  // Handy schon verbunden, bevor die Cast-Seite den Listener registriert hat,
  // kommt nie ein Ereignis und die Anzeige haengt fuer immer auf "warte auf
  // Verbindung".
  //
  // Liefert ALLE Kandidaten mit ihrem aktuellen Wert, nicht einen ausgewaehlten.
  // Welcher davon wirklich die Clients zaehlt, ist nicht dokumentiert und liesse
  // sich hier nur raten - das hat schon einmal nicht funktioniert. Die
  // Auswertung entscheidet stattdessen am WERT (siehe cast/watchSoftAP): die
  // richtige Funktion ist die, die Geraete meldet, sobald welche haengen.
  // Gibt es ueberhaupt Funktionen, die nach einer Client-Abfrage aussehen?
  //
  // Diese Frage MUSS an der Existenz der Funktionen haengen, nicht an ihrem
  // aktuellen Wert. getSoftAPClients liefert nur Eintraege fuer Funktionen, die
  // gerade etwas zurueckgeben - und im Moment der Registrierung haengt noch kein
  // Handy am Hotspot. Wer die Faehigkeit daran misst, kommt zum Schluss "kann
  // das Geraet nicht" und fragt nie wieder nach: die Anzeige bleibt dann fuer
  // immer auf "warte auf Verbindung".
  static hasSoftAPClientFns() {
    return CastTizen.softApClientFnNames().length > 0;
  }

  static getSoftAPClients() {
    const n = network();
    if (!n) return [];
    const names = CastTizen.softApClientFnNames();
    const out = [];
    for (let i = 0; i < names.length; i++) {
      try {
        const v = n[names[i]]();
        if (v !== null && typeof v !== 'undefined') out.push({ fn: names[i], value: v });
      } catch (e) {
        /* Funktion braucht Argumente o.ae. - dann ist sie nicht die gesuchte */
      }
    }
    return out;
  }

  // Laeuft der Hotspot? Das Geraet weiss es selbst (isSoftAPEnabled) - bis jetzt
  // wurde die Funktion nur aufgezaehlt, nie gefragt. Genau das hat gefehlt:
  // rotateSoftAP meldete "ok", obwohl der Hotspot nach dem Neustart unten
  // blieb. Rueckgabe null heisst "keine Auskunft".
  static isSoftApEnabled() {
    const n = network();
    // Ersatzsignal fuer Firmwares ohne isSoftAPEnabled: die Signalstaerke.
    // Am U800F lieferte getSoftAPSignalStrength 4294967295 - das ist 0xFFFFFFFF,
    // also -1 - waehrend der Hotspot lag, und 60, waehrend er sendete. Ein
    // ungueltiger Wert heisst also "Treiber unten".
    if (!n || typeof n.isSoftAPEnabled !== 'function') {
      if (n && typeof n.getSoftAPSignalStrength === 'function') {
        try {
          const dbm = Number(n.getSoftAPSignalStrength());
          if (dbm === 4294967295 || dbm === -1) return false;
        } catch (e) {
          /* keine Auskunft - dann eben null */
        }
      }
      return null;
    }
    try {
      const v = n.isSoftAPEnabled();
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v > 0;
      const t = String(v).trim().toLowerCase();
      if (t === 'true' || t === '1' || t === 'on') return true;
      if (t === 'false' || t === '0' || t === 'off') return false;
      return null;
    } catch (e) {
      logErr('isSoftAPEnabled', e);
      return null;
    }
  }

  // Bringt den Hotspot zurueck, falls er unten ist - mit mehreren Versuchen.
  //
  // enableSoftAP braucht auf dieser Firmware rund zehn Sekunden, bis es mit
  // "Failed to enable Soft AP" aufgibt, und es klappt nicht immer beim ersten
  // Mal. Das laeuft ausschliesslich im Hintergrund, deshalb sind Wartezeiten
  // hier vertretbar - im Ausschaltpfad oder vor einem App-Start haben sie
  // nichts zu suchen.
  //
  // Laeuft schon ein Einschaltversuch, bekommt jeder weitere Aufrufer dessen
  // Ergebnis. Am U800F (19.10.2026) starteten beim Oeffnen der Cast-Seite
  // zwei Versuche in derselben Millisekunde (configureSoftAP und
  // cast/ensureHotspot) - zwei synchrone enableSoftAP und zwei Wartezeiten.
  static ensureSoftApUp() {
    if (CastTizen._softApUpPending) return CastTizen._softApUpPending;
    const run = CastTizen._ensureSoftApUpOnce();
    CastTizen._softApUpPending = run;
    const clear = () => {
      if (CastTizen._softApUpPending === run) CastTizen._softApUpPending = null;
    };
    run.then(clear, clear);
    return run;
  }

  static _ensureSoftApUpOnce() {
    const up = CastTizen.isSoftApEnabled();
    if (up === true) return Promise.resolve(true);

    // HOECHSTENS EIN Versuch pro App-Start, und das ist der ganze Punkt dieser
    // Funktion.
    //
    // enableSoftAP ist SYNCHRON und braucht auf einer Firmware, auf der es
    // scheitert, rund zehn Sekunden bis zur Fehlermeldung. Zehn Sekunden, in
    // denen der JS-Thread im Firmware-Aufruf steht: die App zeichnet nicht,
    // reagiert auf keine Taste, und auch die Notfall-Tastenfolge
    // ROT-GRUEN-GELB-BLAU-x in Home.vue kommt nicht durch. Sie sieht damit
    // eingefroren aus, weil sie es ist.
    //
    // Hier standen vorher drei Versuche mit 2 s Abstand - im schlechtesten Fall
    // ueber dreissig Sekunden Stillstand, und das ausgeloest durch das blosse
    // Oeffnen der Cast-Seite. Das war ein Fehler.
    //
    // Ist der Versuch einmal fehlgeschlagen, wird er in diesem App-Start NICHT
    // wiederholt. Zurueck kommt der Hotspot dann durch einen Neustart des TV -
    // wiederholtes Einschalten hat am Geraet ohnehin nichts geholfen.
    // EINSCHALTEN WIRD IMMER VERSUCHT.
    //
    // Der Merker isSoftApSwitchBroken sperrt nur das AUSSCHALTEN und die
    // Rotation - das sind die Vorgaenge, die den Hotspot verlieren koennen.
    // Einschalten ist dagegen harmlos: im schlimmsten Fall bleibt er aus, wie
    // er schon war. Ihn deshalb nicht mehr zu versuchen war falsch - ein
    // Hotspot, den niemand mehr anfasst, bleibt fuer immer aus.
    //
    // Der Preis ist ein synchroner Aufruf, der je Firmware zwischen 150 ms und
    // rund zehn Sekunden braucht. Deshalb IMMER nur ein Versuch pro Aufruf; wie
    // oft aufgerufen wird, entscheidet der Aufrufer (die Cast-Seite in Abstaenden
    // und mit Anzeige, siehe cast/ensureHotspot).
    console.log('[Cast/Tizen] SoftAP ist aus - Einschaltversuch');
    const ok = CastTizen.ensureSoftApEnabled();
    // null = das Geraet gibt keine Auskunft darueber, ob es geklappt hat.
    if (up === null) return Promise.resolve(null);
    const after = CastTizen.isSoftApEnabled();
    if (after === true) {
      return Promise.resolve(true);
    }
    // Ohne Ausnahme zurueckgekommen: dem Hotspot Zeit geben, bevor er als
    // nicht einschaltbar gilt.
    //
    // Am U800F (07.10.2026) stand isSoftAPEnabled direkt nach enableSoftAP noch
    // auf false - der Merker "nie mehr schalten" wurde gesetzt -, 15 s spaeter
    // lief der Hotspot. Der Merker war also falsch und sperrte danach jeden
    // Passwortwechsel ohne Neustart.
    //
    // Und auch nach der Wartezeit NICHT als defekt markieren, solange
    // enableSoftAP keine Ausnahme geworfen hat: am U800F (19.10.2026, nach dem
    // Cast-Standby-Neustart) brauchte der Hotspot rund 40 s - zwei Wartezeiten
    // liefen leer, der dritte Versuch der Cast-Seite fand ihn laufend. Der
    // dauerhafte Merker waere dort falsch gewesen. Der Aufrufer versucht es
    // ohnehin in Abstaenden weiter (Cast-Seite, cast/ensureHotspot).
    if (ok === true) {
      return CastTizen._waitSoftApUp(SOFTAP_UP_WAIT_MS).then((late) => {
        if (late === true) {
          console.log('[Cast/Tizen] SoftAP nach dem Einschalten verzoegert hochgekommen');
          return true;
        }
        console.log('[Cast/Tizen] SoftAP nach ' + SOFTAP_UP_WAIT_MS + ' ms noch nicht oben - naechster Versuch folgt');
        return false;
      });
    }
    // OHNE Ausnahme und trotzdem aus - genau dieser Fall hat die Absicherung
    // umgangen.
    //
    // Am Geraet: enableSoftAP kam nach 142 ms sauber zurueck
    // ("message data null", kein Fehler), und isSoftAPEnabled stand danach
    // weiter auf false. markSoftApSwitchBroken hing bis jetzt nur an einer
    // geworfenen Ausnahme - der Merker wurde also nie gesetzt, und jede neue
    // Sitzung versuchte es erneut, inklusive Ausschalten in der Rotation.
    //
    // Maßgeblich ist der ZUSTAND, nicht der Rueckgabewert: bleibt der Hotspot
    // nach einem Einschaltversuch aus, kann dieses Geraet ihn nicht einschalten.
    console.log('[Cast/Tizen] ACHTUNG: SoftAP ist AUS und liess sich nicht einschalten (' + ok + ')');
    CastTizen.markSoftApSwitchBroken();
    return Promise.resolve(false);
  }

  // Fragt isSoftAPEnabled jede Sekunde ab, bis true oder die Zeit um ist.
  static _waitSoftApUp(ms) {
    return new Promise((resolve) => {
      const until = Date.now() + ms;
      const tick = () => {
        if (CastTizen.isSoftApEnabled() === true) return resolve(true);
        if (Date.now() >= until) return resolve(false);
        setTimeout(tick, 1000);
      };
      setTimeout(tick, 1000);
    });
  }

  static setSoftAPHidden(hidden) {
    const n = network();
    if (!n || typeof n.setSoftapSSIDHiddenmode !== 'function') return false;
    try {
      n.setSoftapSSIDHiddenmode(hidden ? 1 : 0);
      return true;
    } catch (e) {
      logErr('setSoftAPHidden', e);
      return false;
    }
  }

  // Der Listener, der VERBUNDENE GERAETE meldet.
  //
  // Am Geraet (U800F, Tizen 9.0) abgelesen: webapis.network bringt neben
  // addSoftAPStateChangeListener auch addSoftAPClientInfoListener mit - und das
  // ist der richtige fuer diese Frage. addSoftAPStateChangeListener meldet den
  // Zustand des HOTSPOTS (an/aus), nicht seine Clients: er war registriert und
  // hat nie gefeuert, waehrend ein Handy verbunden war ("Letzte SoftAP-Meldung:
  // noch keine" auf /info). Eine lesende Client-Abfrage gibt es auf dieser
  // Firmware nicht - die Getter liefern nur Channel, SSID, Schluessel,
  // Sicherheitstyp, Signalstaerke, Sichtbarkeit und isSoftAPEnabled.
  // Listener-Ids der Firmware je Callback (add* liefert sie, remove* will sie).
  static _rememberListenerId(cb, id) {
    if (typeof id !== 'number' || id < 0) return;
    CastTizen._listenerIds = (CastTizen._listenerIds || []).filter((e) => e.cb !== cb);
    CastTizen._listenerIds.push({ cb: cb, id: id });
  }

  // Liefert die Id und vergisst sie; unbekannt -> die Funktion selbst, wie bisher.
  static _takeListenerId(cb) {
    const list = CastTizen._listenerIds || [];
    for (let i = 0; i < list.length; i++) {
      if (list[i].cb === cb) {
        const id = list[i].id;
        list.splice(i, 1);
        return id;
      }
    }
    return cb;
  }

  static hasSoftAPClientInfoListener() {
    const n = network();
    return !!(n && typeof n.addSoftAPClientInfoListener === 'function');
  }

  static addSoftAPClientInfoListener(onChange) {
    const n = network();
    if (!n || typeof n.addSoftAPClientInfoListener !== 'function') return -1;
    try {
      const id = n.addSoftAPClientInfoListener(onChange);
      CastTizen._rememberListenerId(onChange, id);
      return id;
    } catch (e) {
      logErr('addSoftAPClientInfoListener', e);
      return -1;
    }
  }

  static removeSoftAPClientInfoListener(onChange) {
    const n = network();
    if (!n || typeof n.removeSoftAPClientInfoListener !== 'function') return false;
    try {
      // Die Firmware will die Id aus add*, nicht die Funktion - mit der
      // Funktion antwortete der BU800 "TypeMismatchError: Invalid long type
      // passed for 'listenerId'", der alte Listener blieb eingetragen und
      // jedes Ereignis kam doppelt.
      n.removeSoftAPClientInfoListener(CastTizen._takeListenerId(onChange));
      return true;
    } catch (e) {
      logErr('removeSoftAPClientInfoListener', e);
      return false;
    }
  }

  static hasSoftAPStateListener() {
    const n = network();
    return !!(n && typeof n.addSoftAPStateChangeListener === 'function');
  }

  static addSoftAPStateChangeListener(onChange) {
    const n = network();
    if (!n || typeof n.addSoftAPStateChangeListener !== 'function') return -1;
    try {
      const id = n.addSoftAPStateChangeListener(onChange);
      CastTizen._rememberListenerId(onChange, id);
      return id;
    } catch (e) {
      logErr('addSoftAPStateChangeListener', e);
      return -1;
    }
  }

  static removeSoftAPStateChangeListener(onChange) {
    const n = network();
    if (!n || typeof n.removeSoftAPStateChangeListener !== 'function') return false;
    try {
      // Die Firmware will die Id aus add*, nicht die Funktion - mit der
      // Funktion antwortete der BU800 "TypeMismatchError: Invalid long type
      // passed for 'listenerId'", der alte Listener blieb eingetragen und
      // jedes Ereignis kam doppelt.
      n.removeSoftAPStateChangeListener(CastTizen._takeListenerId(onChange));
      return true;
    } catch (e) {
      logErr('removeSoftAPStateChangeListener', e);
      return false;
    }
  }

  // Konfiguriert den SoftAP und liefert die EFFEKTIVEN Werte fuer den QR.
  //
  // Samsung hat keinen verlaesslichen Getter fuer SSID/Passwort des SoftAP.
  // Damit der QR garantiert zur echten Broadcast-Konfiguration passt, werden
  // SSID und Passwort aktiv gesetzt und exakt diese Werte zurueckgegeben:
  //   - SSID:     device-Konfig -> lesbarer Wert vom TV -> TV-Name -> "HotelTV";
  //               immer via setSoftAPSSID gesetzt, damit der Broadcast passt.
  //   - Passwort: device-Konfig -> lesbarer Wert vom TV -> generiert (nie leer);
  //               immer via setSoftAPSecurityKey gesetzt.
  // Erzeugt ein NEUES SoftAP-Passwort und schreibt es auf den TV.
  //
  // Notwendig beim Gastwechsel: configureSoftAP bevorzugt den bereits auf dem
  // Geraet gespeicherten Schluessel (getSoftAPSecurityKey). Dadurch bleibt das
  // Passwort nach dem ersten Setzen fuer immer gleich - ein abgereister Gast,
  // der es notiert hat, kaeme weiterhin auf den SoftAP. Hier wird der
  // vorhandene Wert deshalb bewusst uebergangen.
  //
  // Die SSID bleibt unangetastet: sie traegt den Raumnamen und ist kein
  // Geheimnis. Neu vergeben wird sie ohnehin beim naechsten Oeffnen der
  // Cast-Seite (siehe sessionSoftApSsid im cast-Store).
  static rotateSoftAP(newKey) {
    const key = newKey || CastTizen._softApPassword(true);
    // Einen liegenden Hotspot nicht rotieren, sondern erst zurueckholen.
    //
    // Ein neues Passwort wird nur durch einen Neustart des AP wirksam - und
    // einen AP neu zu starten, der gar nicht laeuft, hat ihn am Geraet endgueltig
    // verloren. Kommt er nicht hoch, bleibt der Merker stehen und der naechste
    // Anlauf versucht es erneut; ein altes Passwort auf einem Hotspot, der nicht
    // sendet, ist ohnehin niemandes Zugang.
    if (CastTizen.isSoftApEnabled() === false) {
      console.log('[Cast/Tizen] SoftAP liegt - vor dem Rotieren erst einschalten');
      return CastTizen.ensureSoftApUp().then((up) => {
        if (up === false) {
          return { ok: false, written: false, key: key, detail: 'Hotspot liegt und liess sich nicht einschalten' };
        }
        return CastTizen._rotateSoftApNow(key);
      });
    }
    return CastTizen._rotateSoftApNow(key);
  }

  static _rotateSoftApNow(key) {
    // Kein Passwortwechsel auf einem Geraet, das seinen Hotspot nicht neu
    // starten kann.
    //
    // Ein geschriebener Schluessel wird erst mit einem Neustart des AP oder des
    // TV wirksam. Ohne Neustart entstuende der schlimmste Fall: die Cast-Seite
    // zeigt ein Passwort, das nicht gilt, waehrend der Hotspot weiter mit dem
    // alten sendet - der Gast kaeme nicht ins Netz, und niemand koennte sich
    // erklaeren warum. Deshalb wird hier gar nichts geschrieben.
    //
    // softApActive bleibt damit stehen. Das ist korrekt: die Aufgabe ist NICHT
    // erledigt. Erledigen kann sie nur ein Neustart des TV, und den gibt es
    // ueber den SocketAdmin, die Farbtastenfolge und - wenn eingeschaltet - den
    // Standby-Neustart.
    if (CastTizen.isSoftApSwitchBroken()) {
      console.log(
        '[Cast/Tizen] SoftAP-Passwort NICHT erneuert: dieses Geraet kann den Hotspot nicht neu starten, ' +
          'ein neuer Schluessel wuerde erst nach einem Neustart des TV gelten'
      );
      return Promise.resolve({
        ok: false,
        written: false,
        key: key,
        detail: 'Geraet kann den Hotspot nicht neu starten - Passwortwechsel braucht einen Neustart des TV',
      });
    }
    const written = CastTizen.setSoftAPSecurityKey(key);
    if (!written) {
      console.log('[Cast/Tizen] SoftAP-Passwort nicht geschrieben');
      return Promise.resolve({ ok: false, written: false, key: key, detail: 'setSoftAPSecurityKey fehlgeschlagen' });
    }
    // Ohne Neustart sendet der laufende SoftAP weiter mit dem alten Schluessel -
    // der abgereiste Gast kaeme also genau so weiter ins Netz wie vorher.
    CastTizen.restartSoftAP();
    // Und JETZT nachsehen, ob er wieder laeuft.
    //
    // Vorher galt der Vorgang als erfolgreich, sobald das Passwort geschrieben
    // war. Am Geraet fuehrte das zum schlimmsten Fall: enableSoftAP scheiterte,
    // der Hotspot blieb aus, die App meldete "SoftAP-Zugang fuer neuen Gast
    // erneuert" und loeschte den Merker - der Fehler war damit unsichtbar und
    // wurde nie nachgeholt.
    return CastTizen.ensureSoftApUp().then((up) => {
      const ok = up !== false;
      CastTizen._applied = ok ? { ssid: (CastTizen._applied && CastTizen._applied.ssid) || null, key: key } : null;
      console.log('[Cast/Tizen] SoftAP-Passwort neu vergeben:', ok ? 'ok' : 'Hotspot blieb aus');
      // written: der Schluessel steht auf dem Geraet, auch wenn der Hotspot nicht
      // hochkam. Der Aufrufer MUSS ihn dann trotzdem merken - sonst zeigt die
      // Cast-Seite das alte Passwort an, waehrend auf dem TV das neue gilt.
      return { ok: ok, written: true, key: key, detail: ok ? 'ok' : 'Hotspot liess sich nicht einschalten' };
    });
  }

  static configureSoftAP(config) {
    const c = config || {};

    let ssid = c.ssid || CastTizen.getSoftAPSSID() || CastTizen.getTVName() || 'HotelTV';
    // NICHT vom Geraet lesen: einen verlaesslichen Getter gibt es nicht, und
    // wenn doch einer antwortet, friert er das Passwort dauerhaft ein (der
    // gespeicherte Wert gewaenne dann gegen jedes neue). Massgeblich ist der
    // Wert, den der Aufrufer mitbringt und selbst gemerkt hat.
    let key =
      (typeof c.key === 'string' && c.key.length ? c.key : null) ||
      CastTizen._softApPassword();

    // Nur schreiben, wenn sich seit dem letzten Schreibzugriff dieses App-Starts
    // etwas geaendert hat.
    //
    // Der Grund ist der Neustart weiter unten: der wirft jedes verbundene Handy
    // aus dem Netz. Oeffnet der Gast die Cast-Seite ein zweites Mal (etwa auf dem
    // Weg zurueck von YouTube), waere die Verbindung sonst jedes Mal weg. Der
    // Merker ist bewusst nur zur Laufzeit gueltig: nach einem App-Start ist
    // unbekannt, was auf dem Geraet steht, also wird einmal geschrieben.
    // Steht die gewuenschte Konfiguration bereits auf dem Geraet? Dann NICHT
    // schreiben - und damit auch nicht neu starten.
    //
    // Auf dieser Firmware antworten getSoftAPSSID und getSoftAPSecurityKey mit
    // echten Werten. Sie hier nur zum VERGLEICHEN zu benutzen ist unbedenklich:
    // massgeblich bleibt der Wert, den der Aufrufer mitbringt (siehe unten) -
    // gelesen wird ausschliesslich, um einen unnoetigen Neustart des Hotspots zu
    // vermeiden. Und jeder vermiedene Neustart ist eine Gelegenheit weniger, den
    // AP ganz zu verlieren.
    const onDeviceSsid = CastTizen.getSoftAPSSID();
    const onDeviceKey = CastTizen.getSoftAPSecurityKey();
    // Ohne stripSsidPrefix waere dieser Vergleich auf einer praefigierenden
    // Firmware NIE wahr ('[TV]devo-WUN9' !== 'devo-WUN9'). Die Folge waere
    // genau das, was hier vermieden werden soll: bei jedem App-Start schreiben
    // und den Hotspot neu starten - und jeder Neustart ist eine Gelegenheit,
    // ihn zu verlieren.
    if (CastTizen.stripSsidPrefix(onDeviceSsid) === ssid && onDeviceKey === key) {
      console.log('[Cast/Tizen] SoftAP-Konfiguration steht schon auf dem Geraet - kein Schreibzugriff');
      CastTizen._applied = { ssid: ssid, key: key };
      CastTizen.ensureSoftApUp();
      return { ssid: ssid, key: key, security: c.security || 'WPA' };
    }

    const applied = CastTizen._applied;
    if (applied && applied.ssid === ssid && applied.key === key) {
      // Nichts zu schreiben - aber sicherstellen, dass der Hotspot laeuft. Mit
      // Nachdruck: ein einmal ausgefallener enableSoftAP soll nicht dazu
      // fuehren, dass der Hotspot bis zum Service-Menue aus bleibt.
      CastTizen.ensureSoftApUp();
      return { ssid: ssid, key: key, security: c.security || 'WPA' };
    }

    // Kann dieses Geraet seinen Hotspot nicht neu starten, wird NICHTS
    // geschrieben - sondern gelesen.
    //
    // Ein Schreibzugriff koennte hier nie wirksam werden: der Hotspot sendet
    // weiter mit seiner alten Konfiguration, waehrend im Speicher schon die
    // neue steht. Genau daran ist das Passwort auseinandergelaufen - nach einem
    // Standby-Neustart galt am Hotspot der neu vorbereitete Schluessel, und
    // dann hat diese Funktion ihn mit dem gemerkten alten UEBERSCHRIEBEN.
    // Danach passte gar nichts mehr zusammen: gesendet der neue, gespeichert
    // der alte, angezeigt der alte - und richten konnte es nur ein weiterer
    // Neustart.
    //
    // Also: lesen, anzeigen, QR daraus bauen. Was der TV sendet, ist die
    // Wahrheit, und ohne Neustart koennen wir daran nichts aendern.
    if (CastTizen.isSoftApSwitchBroken()) {
      const liveSsid = CastTizen.getSoftAPSSID();
      const liveKey = CastTizen.getSoftAPSecurityKey();
      console.log(
        '[Cast/Tizen] Hotspot nicht neustartbar - Konfiguration wird NICHT geschrieben, sondern uebernommen: ' +
          'SSID "' + (liveSsid || '?') + '"'
      );
      CastTizen.ensureSoftApUp();
      CastTizen._applied = { ssid: liveSsid || ssid, key: liveKey || key };
      return {
        ssid: liveSsid || ssid,
        key: liveKey || key,
        security: c.security || 'WPA',
        // Der Aufrufer muss seinen Merker nachziehen, sonst zeigt die Seite
        // beim naechsten Start wieder den alten Wert.
        adopted: !!(liveSsid || liveKey),
      };
    }

    // Aktiv auf den TV schreiben, damit Anzeige/QR == tatsaechlicher SoftAP.
    CastTizen.setSoftAPSSID(ssid);
    CastTizen.setSoftAPSecurityKey(key);
    if (typeof c.hidden === 'boolean') CastTizen.setSoftAPHidden(c.hidden);
    // Und den SoftAP neu starten: geschrieben ist die Konfiguration damit, aktiv
    // wird sie erst dadurch. Siehe restartSoftAP.
    const restarted = CastTizen.restartSoftAP();
    if (!restarted.ok && !restarted.tried) CastTizen.ensureSoftApEnabled();
    CastTizen._applied = { ssid: ssid, key: key };

    return { ssid: ssid, key: key, security: c.security || 'WPA' };
  }

  // Liest SSID und Passwort nach dem Schreiben zurueck und vergleicht.
  //
  // Samsung dokumentiert keine Getter, deshalb probiert getSoftAPSSID/
  // getSoftAPSecurityKey mehrere Namen. Antwortet keiner, ist das Ergebnis
  // ausdruecklich "nicht pruefbar" und keine Scheinbestaetigung.
  //
  // Wichtig: das Ergebnis dient NUR der Anzeige/Diagnose. In den Schreibpfad
  // (configureSoftAP) darf der gelesene Wert nicht zurueckfliessen, sonst friert
  // ein einmal gesetztes Passwort fuer immer ein und der Gastwechsel wirkt nicht.
  static verifySoftAP(expectedSsid, expectedKey) {
    const expected = String(expectedSsid == null ? '' : expectedSsid);
    const expectedK = String(expectedKey == null ? '' : expectedKey);
    const actual = CastTizen.getSoftAPSSID();
    const actualK = CastTizen.getSoftAPSecurityKey();
    const ssid = actual === null || typeof actual === 'undefined' ? '' : String(actual);
    const key = actualK === null || typeof actualK === 'undefined' ? '' : String(actualK);
    const readable = ssid.length > 0;
    const keyReadable = key.length > 0;
    // Praefix der Firmware ist keine Abweichung, sondern ein Zusatz: der Rest
    // stimmt. Getrennt gemeldet, damit auf /info nicht 'NEIN' steht, wo das
    // Geraet in Ordnung ist.
    const prefixed = readable && ssid !== expected && CastTizen.stripSsidPrefix(ssid) === expected;
    const matched = readable && ssid === expected;
    const keyMatched = keyReadable && key === expectedK;
    if (prefixed) {
      console.log(
        '[Cast/Tizen] SoftAP-SSID mit Firmware-Praefix: gesetzt "' + expected + '", gesendet "' + ssid + '"'
      );
    } else if (readable && !matched) {
      console.log('[Cast/Tizen] SoftAP-SSID weicht ab: gesetzt "' + expected + '", gelesen "' + ssid + '"');
    }
    if (keyReadable && !keyMatched) {
      console.log('[Cast/Tizen] SoftAP-Passwort weicht ab: gesetzt "' + expectedK + '", gelesen "' + key + '"');
    }
    return Promise.resolve({
      ssid: ssid,
      expected: expected,
      readable: readable,
      matched: matched,
      prefixed: prefixed,
      key: key,
      expectedKey: expectedK,
      keyReadable: keyReadable,
      keyMatched: keyMatched,
      detail: readable
        ? matched
          ? 'ok'
          : 'abweichend'
        : 'kein Getter fuer die SoftAP-SSID (' + (CastTizen.softApApiNames().join(', ') || 'keine SoftAP-API') + ')',
    });
  }

  // =========================================================================
  // Firmware Update
  // =========================================================================

  // Fuehrt ein Firmware-Update aus. Die .bem-Datei muss zuvor auf einem per URL
  // erreichbaren Speicher liegen (spaeter Upload via SocketAdmin -> interner
  // Speicher). Liefert Fortschritt ueber onProgress.
  static updateFirmware(fw, onProgress) {
    return new Promise((resolve, reject) => {
      const sc = systemcontrol();
      if (!sc || typeof sc.updateFirmware !== 'function') {
        reject(new Error('webapis.systemcontrol.updateFirmware missing'));
        return;
      }
      // updateFirmware prueft die Typen selbst und meldet bei jedem fehlenden
      // oder falsch getypten Wert nur "TypeMismatchError: Invalid UpdateInfo".
      // Deshalb vorher pruefen und sagen, WAS fehlt.
      const info = {
        id: fw && fw.id !== undefined ? String(fw.id) : '0',
        fileName: fw && fw.fileName ? String(fw.fileName) : '',
        version: fw && fw.version ? String(fw.version) : '',
        url: fw && fw.url ? String(fw.url) : '',
        totalBytes: fw ? Number(fw.totalBytes) : NaN,
      };
      const missing = [];
      if (!info.fileName) missing.push('fileName');
      if (!info.version) missing.push('version');
      if (!/^https?:\/\//i.test(info.url)) missing.push('url (http/https)');
      if (!(info.totalBytes > 0)) missing.push('totalBytes');
      if (missing.length) {
        reject(new Error('Firmware Update: fehlt/ungueltig: ' + missing.join(', ')));
        return;
      }
      const onchange = function(data) {
        if (onProgress) onProgress(data);
        console.log('[Cast/Tizen] firmware progress: ' + data);
      };
      try {
        if (typeof sc.setUpdateFirmwareListener === 'function') {
          sc.setUpdateFirmwareListener(onchange);
        }
      } catch (e) {
        logErr('setUpdateFirmwareListener', e);
      }
      try {
        sc.updateFirmware(info.id, info.fileName, info.version, info.url, info.totalBytes);
        resolve(true);
      } catch (e) {
        logErr('updateFirmware', e);
        try {
          if (typeof sc.unsetUpdateFirmwareListener === 'function') sc.unsetUpdateFirmwareListener();
        } catch (e2) {
          /* noop */
        }
        reject(e);
      }
    });
  }

  static unsetFirmwareListener() {
    const sc = systemcontrol();
    if (!sc || typeof sc.unsetUpdateFirmwareListener !== 'function') return;
    try {
      sc.unsetUpdateFirmwareListener();
    } catch (e) {
      logErr('unsetUpdateFirmwareListener', e);
    }
  }

  // =========================================================================
  // Settings Cloning (inkl. App-Nachinstallation via Clonefile)
  // =========================================================================

  // Wendet ein Clonefile (CloneEncrypted.sclone) aus dem lokalen Speicher an.
  // Ueber das Clonefile lassen sich auch fehlende Apps (Netflix, YouTube, ...)
  // mit ausrollen. cloneFilePath z.B. "/opt/usr/home/owner/content/Downloads/..".
  static restoreClone(cloneFilePath, onProgress) {
    return new Promise((resolve, reject) => {
      const c = clone();
      if (!c || typeof c.restoreCloneData !== 'function') {
        reject(new Error('webapis.clone.restoreCloneData missing'));
        return;
      }
      const onchange = function(data) {
        if (onProgress) onProgress(data);
        console.log('[Cast/Tizen] clone progress: ' + data);
      };
      // Alten Listener sauber abmelden, dann neu anmelden (siehe Code-Flow PDF).
      try {
        if (typeof c.unSetCloneUpdateStatusListener === 'function') c.unSetCloneUpdateStatusListener();
      } catch (e) {
        /* noop */
      }
      try {
        if (typeof c.setCloneUpdateStatusListener === 'function') c.setCloneUpdateStatusListener(onchange);
      } catch (e) {
        logErr('setCloneUpdateStatusListener', e);
      }
      try {
        c.restoreCloneData(cloneFilePath);
        resolve(true);
      } catch (e) {
        logErr('restoreCloneData', e);
        reject(e);
      }
    });
  }

  static unsetCloneListener() {
    const c = clone();
    if (!c || typeof c.unSetCloneUpdateStatusListener !== 'function') return;
    try {
      c.unSetCloneUpdateStatusListener();
    } catch (e) {
      logErr('unSetCloneUpdateStatusListener', e);
    }
  }

  // =========================================================================
  // Clear Credentials (OTT-Apps, LocalStorage, Browser) beim Check-out
  // =========================================================================

  // Loeschen beim naechsten Neustart scharf schalten. Auf Samsung ist das
  // genau deleteAppPrivateInfo("ON") - eine Einstellung, die erst beim Reboot
  // wirkt und deshalb waehrend des Gastaufenthalts gefahrlos gesetzt werden kann.
  static armClearCredentials() {
    return CastTizen.clearCredentials('ON');
  }

  // value "ON" -> Loeschen beim naechsten Reboot, "OFF" -> deaktivieren.
  static clearCredentials(value) {
    const sc = systemcontrol();
    if (!sc || typeof sc.deleteAppPrivateInfo !== 'function') return false;
    try {
      sc.deleteAppPrivateInfo(value || 'ON');
      return true;
    } catch (e) {
      logErr('deleteAppPrivateInfo', e);
      return false;
    }
  }

  // Kennt diese Firmware das SOFORTIGE Loeschen?
  //
  // Am U800F (Tizen 9.0) nicht: dort gibt es nur deleteAppPrivateInfo("ON"),
  // also das Vormerken fuer den naechsten Neustart. Der Unterschied ist
  // betrieblich wichtig - ohne Sofort-Loeschen verschwinden die Anmeldedaten
  // des Gastes erst mit einem echten Neustart, und im Virtual Standby gibt es
  // keinen. Deshalb steht die Auskunft auf /info.
  // Ist belegt, dass deleteAppPrivateInfo auf diesem Geraet die Anmeldungen
  // der OTT-Apps wirklich entfernt? null = nicht geprueft.
  //
  // Bewusst kein true: die Wirkung haengt an der Firmware. Am U800F (Tizen 9)
  // war YouTube nach scharf geschaltetem deleteAppPrivateInfo("ON") und
  // Neustart abgemeldet; am BU800 (Tizen 6.5, T-NKLBDEUC-1720.7) blieb es am
  // 02.10.2026 angemeldet - scharf geschaltet bestaetigt, rebootDevice
  // angenommen, TV neu gestartet. Ohne diese Funktion fiel Cast/index.js auf
  // true zurueck, und die Diagnose behauptete "nachweisbar wirksam".
  static isClearCredentialsVerified() {
    return null;
  }

  static hasClearCredentialsNow() {
    const sc = systemcontrol();
    return !!(sc && typeof sc.deleteAppPrivateInfoNow === 'function');
  }

  // Sofortiges Loeschen (neuere Firmware). Promise resolved wenn "Done".
  static clearCredentialsNow() {
    return new Promise((resolve, reject) => {
      const sc = systemcontrol();
      if (!sc || typeof sc.deleteAppPrivateInfoNow !== 'function') {
        reject(new Error('deleteAppPrivateInfoNow missing'));
        return;
      }
      try {
        sc.deleteAppPrivateInfoNow(function(data) {
          console.log('[Cast/Tizen] clear credentials now: ' + data);
          resolve(data);
        });
      } catch (e) {
        logErr('deleteAppPrivateInfoNow', e);
        reject(e);
      }
    });
  }

  // =========================================================================
  // High-Level: Check-in / Check-out
  // =========================================================================

  // Vor Check-in: Token init + Google Cast initialisieren + ToS akzeptieren.
  static prepareForCheckIn(token) {
    if (token) CastTizen.initToken(token);
    CastTizen.gcInitialize();
    CastTizen.gcAcceptTermsOfService();
    // Der Token-Status ist Teil der Diagnose - Cache verwerfen, damit das
    // naechste detect() ihn frisch mitnimmt.
    CastTizen._caps = null;
    return true;
  }

  // Bei Check-out: Credentials loeschen + Google Cast finalisieren.
  static finalizeForCheckOut() {
    CastTizen.clearCredentials('ON');
    CastTizen.gcClearData();
    CastTizen.gcFinalize();
    return true;
  }
}



// WEBPACK FOOTER //
// ./src/helpers/Cast/Tizen.js